/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Save, Loader2, Upload, Repeat, X, Plus, Edit2, ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, Info, Lock } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import Decimal from "decimal.js";
import { apiClient } from "@/lib/api";
import { resolveBackHref } from "@/lib/navTracker";
import { composeItemDescription } from "@/lib/itemDescription";
import { composeAutoName } from "@/lib/composeItemName";
import { classifyPresetField, getIdentityOrder } from "@/lib/presetSchemas";
import { validateItemPayload, extractBackendErrors, STRICT_QTY_KEY, STRICT_TAG_KEY } from "@/lib/itemValidation";
import { useAuth } from "@/context/AuthContext";
import { useCollections } from "@/context/CollectionContext";

// Fields with previously-saved values that drive the field-specific
// autosuggest / dropdown-on-focus on Naming + dynamic schema inputs.
// Kept at module scope so both the initial mount fetch and the post-save
// rehydration helper read from the same canonical list.
// Phase B expansion: covers every dropdown the new Phase A schema exposes
// across Wine / Whisky / Spirits, PLUS the legacy keys (Cuvée, Classification,
// Brand, Product) so existing items still hydrate on edit.
// P0 root-preset fix: adds the "memory" fields (Storage Location, Purchase
// Source, Vintage) users repeatedly reuse across items in the SAME cellar.
// All fetches for this list are scoped to the currently selected
// collection_id (see the mount + post-save + collection-change effects) so
// a Wine cellar never surfaces Whisky "Region" / "Age" values and vice
// versa.
const SUGGEST_FIELDS = [
  // Wine
  "Producer", "Country", "Region", "Sub-Region", "Appellation", "Type", "Grape(s)", "Bottle Size",
  // Whisky (CDI locked — legacy keys removed)
  "Distillery", "Age", "Cask Type", "ABV", "Bottling Year", "Release Type",
  // Spirits (CDI industry-standard order)
  "Producer / Distillery", "Item Name",
  // Legacy keys — retained only so existing items surface in dropdowns/
  // pre-rename search results. Not part of any primary block.
  "Brand / Producer", "Spirit Type",
  "Cuvée", "Classification", "Brand", "Product",
  // Memory fields — reused across items within a cellar. Scoped by
  // collection_id at fetch time so cross-cellar values don't leak.
  "Storage Location", "Purchase Source", "Vintage",
];

// Module-scope set tracking which categorical-field names have already
// surfaced the one-time "Added X to FieldName" toast in THIS browser tab.
// Module scope (not React state) is intentional — it survives unmount /
// remount of <ItemEntry> as the user navigates between create / edit /
// list views inside the same session, but resets on a hard refresh.
const _addedFieldToastShown = new Set();

import BottomNav from "@/components/BottomNav";
import ComboBox from "@/components/ComboBox";

/**
 * ItemEntry — Step 4 rebuild.
 *
 * Minimal create flow:
 *   1. Pick a Category.
 *   2. Dynamic fields from the category's schema render below.
 *   3. Save → POST /api/items.
 *
 * Item.collection_id is auto-set server-side from the chosen category's
 * collection_id (see backend POST /items handler).
 */

// Translate strict backend / itemValidation.js error keys into the flat
// `fieldErrors` shape ItemEntry already renders. `item_data.Total Bottles`
// becomes `Total Bottles`, `cost_price_ex_gst` becomes `Buy Price (Ex GST)`,
// etc. Kept top-level so the mapping is trivially discoverable from a
// review perspective.
const _STRICT_ERROR_FIELD_MAP = {
  "item_data.Total Bottles": "Total Bottles",
  "item_data.Tag": "Tag",
  "cost_price_ex_gst": "Buy Price (Ex GST)",
  "estimated_value_ex_gst": "Market Value (Ex GST)",
  "category_id": "_category",
};

function mapStrictErrorsToFields(strictErrors) {
  const out = {};
  for (const [k, v] of Object.entries(strictErrors)) {
    out[_STRICT_ERROR_FIELD_MAP[k] || k] = v;
  }
  return out;
}

export default function ItemEntry() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const location = useLocation();
  const isEdit = Boolean(editId);
  // CDI Master Rule — consume the tier-filtered collections from the
  // context so trial/basic users never see Premium collections in the
  // Edit-Item / New-Item dropdown. Admins get the full list because
  // CollectionContext folds is_admin into canPremiumUI.
  const { collections } = useCollections();
  const [categories, setCategories] = useState([]);
  const [collectionId, setCollectionId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  // Tracks the previous categoryId so the schema-load effect can detect a
  // user-initiated category switch (as opposed to the initial mount /
  // Edit-mode load) and wipe stale form values whose keys aren't in the
  // new category's schema. See the schema-load effect for the full rule.
  const prevCategoryIdRef = useRef("");
  // Edit-mode snapshot — captured once when the existing item loads. Lets
  // us restore the full item_data / collection / category if the user
  // wanders off to a foreign collection type (Wine → Whisky) and then
  // returns to the item's ORIGINAL collection. Without this, the
  // cross-type wipe would leave them with an empty form even after
  // navigating back "home". Populated inside the Edit-mode load effect
  // below; read by the Collection dropdown's onChange handler.
  const editSnapshotRef = useRef(null); // { collectionId, categoryId, values, image, images, buyEx, buyInc, marketValueEx, marketValueInc, currencyCode, pricingMode, purchasePrice, estimatedValue }
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  // P — Category Swap Preview: how many `values` keys were dropped by
  // the most-recent prune-on-category-swap pass. Rendered inline beside
  // the Category dropdown as an informational message. Reset to 0 on
  // the very first schema load so Edit-mode hydration doesn't briefly
  // flash "N fields will be cleared". Never blocks the change.
  const [lastPrunedCount, setLastPrunedCount] = useState(0);
  // Field-level validation errors (keyed by field_name). Surfaced inline
  // below each input; cleared as the user fixes the value. Populated by
  // validate() on submit and validateField() on blur.
  const [fieldErrors, setFieldErrors] = useState({});
  // Auto-growing presets — every text value typed anywhere in the app
  // becomes a `<datalist>` suggestion for the same field name everywhere.
  // Map { field_name: [distinct_values_sorted] }.
  const [presets, setPresets] = useState({});
  // Existing distinct item names from /api/items/names — powers the
  // Item Name autosuggestion ComboBox (case-insensitive substring match,
  // top 10 results) so repeat data-entry is much faster.
  const [itemNames, setItemNames] = useState([]);
  // Per-field distinct values from /api/items/field-values — powers the
  // field-specific autosuggest / dropdown-on-focus on the Naming section
  // (Producer / Cuvée / Classification / Brand / Product / Region) and on
  // certain dynamic-schema text fields (Region / Sub-Region / Bottle Size).
  // Shape: { "Producer": ["Domaine Anne Gros", ...], ... }. Empty fields
  // fall back to []; an unknown field name reads as [].
  const [fieldValues, setFieldValues] = useState({});
  // Session-only optimistic markers — tracks values added via "+ Add"
  // that have NOT been confirmed by a backend round-trip yet. Shape:
  // { "<fieldName>": Set<string of normalised value> }. Cleared after a
  // successful save (post-save rehydrate brings the persisted set from
  // the server). Used purely for the italic ★ marker in ComboBox lists.
  // Never written to the wire, never serialised — pure UI state.
  const [optimisticValues, setOptimisticValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [loadingItem, setLoadingItem] = useState(isEdit);
  // Image — only used in edit mode; pre-filled from the item.
  const [image, setImage] = useState("");
  const [images, setImages] = useState([]); // additional images beyond `image`
  const [imageTypeError, setImageTypeError] = useState("");
  const [imagesDirty, setImagesDirty] = useState(false);
  const [imageDirty, setImageDirty] = useState(false);
  // Currency fields — top-level Item attributes (not dynamic schema)
  const [purchasePrice, setPurchasePrice] = useState("");
  const [estimatedValue, setEstimatedValue] = useState("");
  // Unified pricing — Buy/Sell × Ex/Inc GST (canonical fields, backed by
  // `cost_price_ex_gst` / `cost_price_inc_gst` / `estimated_value_ex_gst` /
  // `estimated_value_inc_gst` on the Item model). On edit-load these fall
  // back to legacy `purchase_price` / `estimated_value` so existing items
  // populate cleanly.
  const [buyEx, setBuyEx] = useState("");
  const [buyInc, setBuyInc] = useState("");
  const [marketValueEx, setMarketValueEx] = useState("");
  const [marketValueInc, setMarketValueInc] = useState("");
  // resolveValuation() spec (2026-02): explicit source + manual input
  // sit alongside the market inputs. Persisted as top-level Item
  // fields so `computeItemValuation` can drive Sell / Valuation.
  const [manualValueEx, setManualValueEx] = useState("");
  const [valuationSource, setValuationSource] = useState(""); // "" | "Market" | "Manual" | "Computed"
  // Currency (Step 2, 2026-02) — item currency now ALWAYS derives from
  // the global setting written by Settings → Collection Currencies
  // (`cdi.settings.currency`). The per-item Currency dropdown has been
  // removed from the Pricing UI; state is retained for existing write
  // paths (payload, snapshot, tax config keying) but is no longer
  // user-editable on this screen. Defaults to "NZD" if the global
  // setting is absent so nothing regresses on fresh installs.
  const [currencyCode, setCurrencyCode] = useState(() => {
    try {
      return (
        (typeof window !== "undefined" &&
          localStorage.getItem("cdi.settings.currency")) ||
        "NZD"
      );
    } catch (_e) { return "NZD"; }
  });
  // Manual FX rates (per item) — only meaningful when currencyCode !== "NZD".
  // Applied at the aggregation layer: cost_nzd = cost * fx_rate_purchase,
  // value_nzd = value * (fx_rate_sale || fx_rate_purchase). Optional; both
  // default to 1 which is a no-op for NZD items and legacy items saved
  // before this feature shipped.
  const [fxRatePurchase, setFxRatePurchase] = useState("");
  const [fxRateSale, setFxRateSale] = useState("");

  // Per-item GST pricing-mode toggle. "inc" = user enters prices including
  // GST and the Ex variants are auto-calculated; "ex" = the inverse. Defaults
  // to the user's global Settings preference (cdi.defaultPricingMode) on
  // create; in edit mode we hydrate from item_data._pricing_mode (saved
  // alongside other dynamic fields — no backend schema change), falling back
  // to the same inference rule used by Item Detail's "Entered as:" footer.
  const [pricingMode, setPricingMode] = useState(() => {
    try {
      const v = localStorage.getItem("cdi.defaultPricingMode");
      return v === "inc" ? "inc" : "ex";
    } catch { return "ex"; }
  });
  // Standard NZ GST rate (15%) — same rate used implicitly across the app.
  // All conversion + rounding is delegated to Decimal.js (arbitrary-precision
  // decimal arithmetic). This guarantees:
  //   • Ex → Inc → Ex always returns the original to 2dp (no float drift).
  //   • Every intermediate step is rounded HALF_UP to 2dp.
  //   • Sub-cent paste values (e.g. "10.123") collapse to 2dp at every entry
  //     point: keystroke, blur, mode-toggle, save, and load.
  // Tax NAME map (Step 2 industry-standard grouping — GST/VAT/Sales Tax
  // /Tax fallback). RATE is no longer keyed by currency; it is read
  // dynamically from `cdi.settings.taxPercent` written by Settings →
  // Collection Currencies. New items snapshot the numeric percent so
  // Item Detail can recompute inc↔ex with the item's own stored rate
  // (see itemValue.js + ItemDetail.jsx pricing rows).
  const taxNameMap = {
    NZD: "GST",       AUD: "GST",
    USD: "Sales Tax", CAD: "Sales Tax",
    GBP: "VAT",       EUR: "VAT",
  };
  const taxLabel = taxNameMap[currencyCode] || "Tax";
  // Global tax percent — promoted to React state (2026-02-25) so
  // Settings hydration + user edits apply immediately without a
  // reload. Seeded from localStorage so the first render still has
  // the last-saved value even before the /settings/global fetch
  // resolves. Mirrored back into localStorage by the Settings page
  // and by the hydration useEffect below.
  const [globalTaxPercent, setGlobalTaxPercent] = useState(() => {
    try {
      const raw = typeof window !== "undefined"
        ? localStorage.getItem("cdi.settings.taxPercent")
        : null;
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch (_e) { return 0; }
  });
  // Backend hydrate (2026-02-25) — on mount, fetch the caller's
  // persisted global settings and update BOTH the React state
  // variables (currencyCode / globalTaxPercent) AND the existing
  // localStorage mirrors (`cdi.settings.currency`,
  // `cdi.settings.taxPercent`) so:
  //   • the current screen re-renders with the new tax / currency
  //     immediately (no reload needed)
  //   • every other surface that reads localStorage synchronously
  //     (Analytics, ItemDetail, RecordSaleModal, itemValue.js) sees
  //     the same values on their next render.
  // Silent fallback on offline / not-signed-in — keeps whatever the
  // seed initialisers already loaded from localStorage.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get("/settings/global");
        if (cancelled || !data) return;
        const code = String(data.currency || "").toUpperCase();
        if (code) {
          setCurrencyCode(code);
          try { localStorage.setItem("cdi.settings.currency", code); } catch (_e) { /* private mode */ }
        }
        const pct = Number(data.tax_percent);
        if (Number.isFinite(pct) && pct >= 0) {
          setGlobalTaxPercent(pct);
          try { localStorage.setItem("cdi.settings.taxPercent", String(pct)); } catch (_e) { /* private mode */ }
        }
      } catch { /* offline / not-signed-in — keep localStorage seed */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Dynamic tax multiplier — Step 4 (2026-02). NO hard-coded 1.15 /
  // 1.20 constants anywhere. `taxRate` is a Decimal fraction (e.g.
  // 0.15 for 15%), `taxMultiplier` = 1 + taxRate. Both are used by
  // toIncFromEx / toExFromInc below with exact 2dp HALF_UP rounding.
  const taxRate = new Decimal(String(globalTaxPercent)).dividedBy(100);
  const taxMultiplier = new Decimal("1").plus(taxRate);
  // Kept the historical constant names removed in Step 4 (2026-02);
  // downstream call sites now read `taxMultiplier` defined above. The
  // helpers below become identity functions when taxRate === 0 so
  // switching to a 0-tax currency in Settings is a no-op.
  // Quantise any value (Decimal | string | number) to 2dp string with HALF_UP.
  // Empty/invalid input → "" so the input stays blank instead of "0.00".
  const q2 = (v) => {
    if (v === "" || v === null || v === undefined) return "";
    try {
      const d = new Decimal(v);
      if (d.isNaN() || !d.isFinite()) return "";
      return d.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2);
    } catch { return ""; }
  };
  const toIncFromEx = (ex) => {
    if (ex === "" || ex === null || ex === undefined) return "";
    try {
      const d = new Decimal(ex);
      if (d.isNaN() || !d.isFinite()) return "";
      return d.times(taxMultiplier).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2);
    } catch { return ""; }
  };
  const toExFromInc = (inc) => {
    if (inc === "" || inc === null || inc === undefined) return "";
    try {
      const d = new Decimal(inc);
      if (d.isNaN() || !d.isFinite()) return "";
      return d.dividedBy(taxMultiplier).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2);
    } catch { return ""; }
  };
  // Linked-input handlers — always update the typed field AND auto-calc the
  // other so the canonical 4-field payload (cost_price_ex_gst /
  // cost_price_inc_gst / estimated_value_ex_gst / estimated_value_inc_gst)
  // stays populated on Save without any change to backend logic.
  const onBuyTyped = (val) => {
    if (pricingMode === "inc") { setBuyInc(val); setBuyEx(toExFromInc(val)); }
    else { setBuyEx(val); setBuyInc(toIncFromEx(val)); }
  };
  const onSellTyped = (val) => {
    if (pricingMode === "inc") { setMarketValueInc(val); setMarketValueEx(toExFromInc(val)); }
    else { setMarketValueEx(val); setMarketValueInc(toIncFromEx(val)); }
  };
  // Blur-time normalisation — clamps the visible value to 2dp via Decimal
  // and re-derives the counterpart from the normalised value. Catches paste
  // of "10.123" → "10.12", and guarantees the saved value is the same
  // value the user sees in the input.
  const onBuyBlur = () => {
    if (pricingMode === "ex") {
      const ex = q2(buyEx);
      setBuyEx(ex);
      setBuyInc(toIncFromEx(ex));
    } else {
      const inc = q2(buyInc);
      setBuyInc(inc);
      setBuyEx(toExFromInc(inc));
    }
  };
  const onSellBlur = () => {
    if (pricingMode === "ex") {
      const ex = q2(marketValueEx);
      setMarketValueEx(ex);
      setMarketValueInc(toIncFromEx(ex));
    } else {
      const inc = q2(marketValueInc);
      setMarketValueInc(inc);
      setMarketValueEx(toExFromInc(inc));
    }
  };
  const onPricingModeChange = (next) => {
    setPricingMode(next);
    // GST/tax auto-conversion — 2026-02-24. On every mode flip, ALWAYS
    // re-derive the newly-visible side from the previously-visible side
    // using the user's tax rate from Settings (`taxMultiplier` above, fed
    // by `cdi.settings.taxPercent`). UI-only; stored values are untouched
    // until the user clicks "Update Item".
    //   Switch → "inc" : buyInc = buyEx  * (1 + taxRate)   [Ex was visible]
    //   Switch → "ex"  : buyEx  = buyInc / (1 + taxRate)   [Inc was visible]
    if (next === "inc") {
      if (buyEx  !== "") setBuyInc(toIncFromEx(buyEx));
      if (marketValueEx !== "") setMarketValueInc(toIncFromEx(marketValueEx));
    } else {
      if (buyInc !== "") setBuyEx(toExFromInc(buyInc));
      if (marketValueInc !== "") setMarketValueEx(toExFromInc(marketValueInc));
    }
  };

  // Load categories + collections on mount
  const collectionScopeRef = useRef("");
  useEffect(() => {
    let cancelled = false;
    // Task C-Fix: when ItemEntry is opened from a specific collection
    // (Add Item button or empty-state CTA), the URL carries
    // ?collection_id=<id>. Honour it: pin the form to that collection and
    // pick a category INSIDE it (default to the seeded one) so the user
    // never silently lands on the global Wine collection.
    const qp = new URLSearchParams(location.search);
    const urlCollectionId = qp.get("collection_id") || "";
    (async () => {
      try {
        // Resolve the collection scope BEFORE issuing suggestion queries so
        // /items/field-values only returns values from items inside the
        // same cellar (Wine Region ≠ Whisky Region). Priority mirrors the
        // create-mode default logic below: URL override > user default >
        // sticky last-used category's collection > no scope.
        const colResPre = await apiClient.get("/collections").catch(() => ({ data: [] }));
        const cols = colResPre.data || [];
        let scopeCollectionId = "";
        if (urlCollectionId && cols.some((c) => c.id === urlCollectionId)) {
          scopeCollectionId = urlCollectionId;
        } else {
          const def = cols.find((c) => c.is_default);
          if (def?.id) scopeCollectionId = def.id;
        }
        const nameQS = scopeCollectionId
          ? `?collection_id=${encodeURIComponent(scopeCollectionId)}`
          : "";
        const [catRes, namesRes, ...valRes] = await Promise.all([
          apiClient.get("/categories"),
          apiClient.get(`/items/names${nameQS}`),
          ...SUGGEST_FIELDS.map((f) => {
            const scopeQS = scopeCollectionId
              ? `&collection_id=${encodeURIComponent(scopeCollectionId)}`
              : "";
            return apiClient.get(`/items/field-values?field=${encodeURIComponent(f)}${scopeQS}`)
              .catch(() => ({ data: { values: [] } }));
          }),
        ]);
        if (!cancelled) {
          setCategories(catRes.data || []);
          // collections come from the tier-filtered context — no local state.
          setItemNames(namesRes.data?.names || []);
          const fv = {};
          SUGGEST_FIELDS.forEach((f, i) => {
            fv[f] = valRes[i]?.data?.values || [];
          });
          setFieldValues(fv);
          // Prime the scope-change guard so the collection-change effect
          // doesn't re-issue the same query when it observes setCollectionId
          // firing below (either from URL override, user default, or the
          // sticky last-used category's collection). This is safe: the
          // effect always fetches when scope ACTUALLY changes.
          collectionScopeRef.current = scopeCollectionId;
          // On create mode only: pick sane defaults.
          if (!isEdit) {
            // 1) Collection. URL override wins; otherwise fall back to the
            //    user's globally-set default collection.
            const def = (colResPre.data || []).find((c) => c.is_default);
            const initialCollectionId =
              urlCollectionId && (colResPre.data || []).some((c) => c.id === urlCollectionId)
                ? urlCollectionId
                : (def?.id || "");
            if (initialCollectionId) setCollectionId((cur) => cur || initialCollectionId);

            // 2) Category. If the URL pinned us to a specific collection,
            //    pick a category INSIDE that collection (skip the global
            //    sticky last-used category — it almost always points to a
            //    different collection's category). Prefer the seeded
            //    category (created first → lowest order / earliest in the
            //    array). If no URL override, use the sticky last-used.
            const allCats = catRes.data || [];
            if (urlCollectionId) {
              const inCol = allCats.filter((c) => c.collection_id === urlCollectionId);
              if (inCol.length > 0) {
                setCategoryId((cur) => cur || inCol[0].id);
              }
            } else {
              let lastCat = "";
              try { lastCat = localStorage.getItem("cdi.lastCategoryId") || ""; } catch (_e) { /* noop */ }
              if (lastCat) {
                const found = allCats.find((c) => c.id === lastCat);
                if (found && (!initialCollectionId || found.collection_id === initialCollectionId)) {
                  setCategoryId((cur) => cur || lastCat);
                  if (found.collection_id) setCollectionId((cur) => cur || found.collection_id);
                }
              }
            }
          }
        }
      } catch {
        toast.error("Failed to load categories.");
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit]);

  // Re-fetch scoped autosuggest values whenever the active collection
  // changes (create-mode switch, edit-mode hydrate, or Collection dropdown
  // change). Without this, a user switching from Wine to Whisky would keep
  // seeing Wine Regions in the dropdown until a hard reload. Scope keeps
  // Wine ≠ Whisky. On empty scope (very rare — user cleared the collection)
  // we short-circuit rather than issuing a global query, so cross-cellar
  // values never leak in through the ItemEntry form.
  useEffect(() => {
    if (!collectionId) return;
    if (collectionScopeRef.current === collectionId) return;
    collectionScopeRef.current = collectionId;
    let cancelled = false;
    (async () => {
      try {
        const nameQS = `?collection_id=${encodeURIComponent(collectionId)}`;
        const [namesRes, ...valRes] = await Promise.all([
          apiClient.get(`/items/names${nameQS}`).catch(() => ({ data: { names: [] } })),
          ...SUGGEST_FIELDS.map((f) =>
            apiClient
              .get(`/items/field-values?field=${encodeURIComponent(f)}&collection_id=${encodeURIComponent(collectionId)}`)
              .catch(() => ({ data: { values: [] } }))
          ),
        ]);
        if (cancelled) return;
        setItemNames(namesRes.data?.names || []);
        setFieldValues(() => {
          const next = {};
          SUGGEST_FIELDS.forEach((f, i) => {
            next[f] = valRes[i]?.data?.values || [];
          });
          return next;
        });
        // Any optimistic markers were relative to the previous scope — clear.
        setOptimisticValues({});
      } catch {
        /* silent — suggestions are a nicety, not a blocker */
      }
    })();
    return () => { cancelled = true; };
  }, [collectionId]);

  // In edit mode: load the existing item once.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get(`/items/${editId}`);
        if (cancelled) return;
        setCategoryId(data.category_id || "");
        setCollectionId(data.collection_id || "");
        setValues(() => {
          const incoming = data.item_data || {};
          // Backward-compat: legacy `Case Type` sentinels (Full Case /
          // Partial Case / Single Bottles) are folded into the new
          // vocab so old items don't render the retired values. The
          // Case Mode / Bottle Count derivation is now done at SAVE
          // time (see `buildItemData` — Fix #8 Part 1), so we no
          // longer force `Case Mode: "Single Bottle"` or
          // `Bottle Count: 1` on load. Legacy items that lack those
          // keys stay clean until the user actually saves.
          const next = { ...incoming };
          if (next["Case Type"] === "Single Bottles" || next["Case Type"] === "Full Case" || next["Case Type"] === "Partial Case") {
            next["Case Type"] = "None";
          }
          // 2026-02 hydration fallback (option B) — legacy / imported
          // items whose `item_data` identity fields are blank but which
          // carry data on top-level Item columns (`item_name`, `brand`,
          // `year`) or embed identity in the composed item_name string
          // ("Producer Region Grape Vintage ABV Bottle Size") end up
          // rendering an empty Edit form. The fix parses the top-level
          // string (regex-safe extractions ONLY: Vintage / ABV / Bottle
          // Size) and mirrors top-level `brand` / `year` when the
          // corresponding item_data slot is empty/null. Producer,
          // Region, Appellation, Grape(s) etc. cannot be reliably
          // extracted from a free-form title so they remain blank
          // (correct: DB has no source). Read-only — no writes back to
          // the item until the user saves.
          const isBlank = (v) => v === undefined || v === null || v === "" ||
            (typeof v === "string" && v.trim() === "");
          const rawName = String(data.item_name || data.name || "").trim();
          const setIfBlank = (k, v) => {
            if (v !== undefined && v !== null && v !== "" && isBlank(next[k])) {
              next[k] = v;
            }
          };
          // Item Name — fall back to the top-level composed name so
          // the "Item Name" input round-trips whatever the user
          // originally saved (even if item_data["Item Name"] is empty).
          if (rawName) setIfBlank("Item Name", rawName);
          // Vintage — first 4-digit year in the item_name (1900-2099).
          // Skipped if item_data.Vintage already carries a number.
          if (rawName && isBlank(next["Vintage"])) {
            const m = rawName.match(/\b(19\d{2}|20\d{2})\b/);
            if (m) next["Vintage"] = Number(m[1]);
          }
          // ABV — first "<num>%" occurrence (e.g. "15%", "12.5%").
          if (rawName && isBlank(next["ABV"])) {
            const m = rawName.match(/\b(\d{1,3}(?:\.\d+)?)\s*%/);
            if (m) next["ABV"] = `${m[1]}%`;
          }
          // Bottle Size — first "<num>ml" or "<num>cl" occurrence
          // (e.g. "750ml", "1000ml", "50cl"). Case-insensitive.
          if (rawName && isBlank(next["Bottle Size"])) {
            const m = rawName.match(/\b(\d{2,4})\s*(ml|cl|l)\b/i);
            if (m) next["Bottle Size"] = `${m[1]}${m[2].toLowerCase()}`;
          }
          // Top-level column mirrors (only when data actually exists).
          //   brand → Producer (Wine) / Distillery (Whisky) / Brand (Spirits)
          //   year  → Vintage
          // Each target is filled only when currently blank; the
          // schema-load useEffect below will prune keys that don't
          // belong to the active category so cross-type contamination
          // is impossible.
          setIfBlank("Producer",   data.brand);
          setIfBlank("Distillery", data.brand);
          setIfBlank("Brand",      data.brand);
          if (data.year && isBlank(next["Vintage"])) next["Vintage"] = Number(data.year);

          // 2026-02-26 — Quantity Migration Step 2 hydration tail.
          // Prefer the structured top-level columns as the source of
          // truth for quantity. When the backend row carries them, they
          // overwrite the (potentially stale) item_data mirror so the
          // form always shows what the DB actually holds. Full Cases /
          // Loose Bottles tiles are derived (floor / mod) at render
          // time from these two values — no explicit hydration needed.
          if (data.total_units !== undefined && data.total_units !== null) {
            next["Total Bottles"] = data.total_units;
          }
          if (data.case_size !== undefined && data.case_size !== null) {
            next["Case Size"] = data.case_size;
          }
          // 2026-02-26 protect legacy user-typed names — if the
          // loaded Item Name doesn't match ANY of the three
          // composer outputs, the user must have typed it manually.
          // Pin `Name Override = true` so the auto-build useEffects
          // below (Wine / Whisky / Spirits) leave it alone. When
          // the saved name matches at least one auto-composition,
          // no override is set and the composer stays free to
          // re-render on identity edits.
          const savedName = String(next["Item Name"] || "").trim();
          if (savedName) {
            const autoWine = composeAutoName(next, { type_id: "wine" });
            const autoWhisky = composeAutoName(next, { type_id: "whisky" });
            const autoSpirits = composeAutoName(next, { type_id: "spirits" });
            const matches = [autoWine, autoWhisky, autoSpirits].some(
              (a) => a && String(a).trim() === savedName,
            );
            if (!matches) next["Name Override"] = true;
          }
          return next;
        });
        setImage(data.image || "");
        setImages(Array.isArray(data.images) ? data.images.filter(Boolean) : []);
        setPurchasePrice(
          data.purchase_price === 0 || data.purchase_price ? String(data.purchase_price) : ""
        );
        setEstimatedValue(
          data.estimated_value === 0 || data.estimated_value ? String(data.estimated_value) : ""
        );
        // Unified pricing — populate the 4 GST-split fields; fall back to
        // legacy `purchase_price` (→ Buy Inc) and `estimated_value` (→ Sell Ex)
        // so existing items load cleanly under the new schema. Every loaded
        // value is quantised to 2dp via Decimal so the input renders the
        // exact saved value with no float dust.
        const initNum = (v, fallback = "") => {
          const raw = (v === 0 || v) ? v : ((fallback === 0 || fallback) ? fallback : "");
          return q2(raw);
        };
        setBuyEx(initNum(data.cost_price_ex_gst));
        setBuyInc(initNum(data.cost_price_inc_gst, data.purchase_price));
        setMarketValueEx(initNum(data.estimated_value_ex_gst, data.estimated_value));
        setMarketValueInc(initNum(data.estimated_value_inc_gst));
        setManualValueEx(initNum(data.manual_valuation_ex_gst));
        setValuationSource(data.valuation_source || "");
        setCurrencyCode(data.currency_code || "NZD");
        // FX rates hydrate directly from the server value — no `||`
        // fallback. Historic bug: using `data.fx_rate_sale || ""`
        // treated a saved 0 as falsy and reverted to the previous
        // value on reopen. `?? ""` only substitutes null/undefined
        // (legacy items pre-feature) so genuine 0/1/1.68 all round-
        // trip verbatim.
        setFxRatePurchase(
          data.fx_rate_purchase === undefined || data.fx_rate_purchase === null
            ? ""
            : String(data.fx_rate_purchase)
        );
        setFxRateSale(
          data.fx_rate_sale === undefined || data.fx_rate_sale === null
            ? ""
            : String(data.fx_rate_sale)
        );
        // Pricing-mode hydration: prefer explicit `_pricing_mode` saved
        // inside item_data; else infer from existing fields the same way
        // Item Detail's "Entered as:" footer does (presence of inc-GST data).
        const savedMode = (data.item_data || {})._pricing_mode;
        const resolvedMode = (savedMode === "inc" || savedMode === "ex")
          ? savedMode
          : ((Number(data.gst) > 0 || Number(data.cost_price_inc_gst) > 0) ? "inc" : "ex");
        setPricingMode(resolvedMode);

        // Snapshot for cross-type navigation rehydration. Captures the
        // hydrated state exactly as it landed on the form so we can put
        // it back when the user returns to this item's home collection.
        editSnapshotRef.current = {
          collectionId: data.collection_id || "",
          categoryId: data.category_id || "",
          values: { ...(data.item_data || {}) },
          image: data.image || "",
          images: Array.isArray(data.images) ? data.images.filter(Boolean) : [],
          buyEx: initNum(data.cost_price_ex_gst),
          buyInc: initNum(data.cost_price_inc_gst, data.purchase_price),
          marketValueEx: initNum(data.estimated_value_ex_gst, data.estimated_value),
          marketValueInc: initNum(data.estimated_value_inc_gst),
          currencyCode: data.currency_code || "NZD",
          pricingMode: resolvedMode,
          purchasePrice: data.purchase_price === 0 || data.purchase_price ? String(data.purchase_price) : "",
          estimatedValue: data.estimated_value === 0 || data.estimated_value ? String(data.estimated_value) : "",
        };
      } catch {
        if (!cancelled) toast.error("Failed to load item.");
      } finally {
        if (!cancelled) setLoadingItem(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, editId]);

  // Load fields whenever the chosen category changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!categoryId) {
        if (!cancelled) { setFields([]); }
        return;
      }
      if (!cancelled) setLoadingFields(true);
      try {
        const { data } = await apiClient.get(`/categories/${categoryId}/fields`);
        if (cancelled) return;
        setFields(data || []);
        // Category switching cleanup (Phase C, Master-Template aware):
        //
        // Master Templates guarantee that every Wine / Whisky / Spirits
        // category shares one unified field list. When the user switches
        // Italy ↔ France (both Wine) or Scotland ↔ Japan (both Whisky),
        // we PRESERVE all values — nothing to wipe, the same fields are
        // still on screen. User-added custom fields carry across sibling
        // categories too, so a "Cellar Bay" field the user added on
        // Italy still shows and holds its value after switching to
        // France.
        //
        // When the transition crosses master types (Wine → Whisky) OR
        // moves to/from a non-templated collection type (Wine → Coins),
        // we fall back to the plumbing-only hard wipe so foreign
        // identity fields don't linger.
        //
        // Skipped on the very first schema load (prevCat empty) so
        // Edit mode's item_data hydration is never nuked.
        const prevCat = prevCategoryIdRef.current;
        if (prevCat && prevCat !== categoryId) {
          const oldCatObj = categories.find((c) => c.id === prevCat);
          const newCatObj = categories.find((c) => c.id === categoryId);
          const oldCol = oldCatObj ? collections.find((c) => c.id === oldCatObj.collection_id) : null;
          const newCol = newCatObj ? collections.find((c) => c.id === newCatObj.collection_id) : null;
          const MASTER_TYPES = new Set(["wine", "whisky", "spirits"]);
          const oldKey = oldCol && MASTER_TYPES.has(String(oldCol.type_id || "").toLowerCase())
            ? String(oldCol.type_id).toLowerCase() : null;
          const newKey = newCol && MASTER_TYPES.has(String(newCol.type_id || "").toLowerCase())
            ? String(newCol.type_id).toLowerCase() : null;
          const sameTypeMaster = oldKey && newKey && oldKey === newKey;

          // Plumbing that ALWAYS survives a category swap (regardless of
          // same-type vs cross-type). These keys are non-schema
          // metadata / legacy quantity plumbing the DB still expects.
          const preservedKeys = new Set([
            "Name Override",
            // Model A quantity plumbing (new):
            "Total Bottles", "Case Size",
            // Legacy quantity keys — persisted for round-trip on old items.
            "Case Mode", "Bottle Count", "Units in Case", "Case Type",
          ]);

          // Prune-on-Category-Swap. Build the set of field names the NEW
          // category schema actually declares. Any value key NOT in that
          // set — and not in the preserved plumbing above — is dropped
          // so stale numeric / array / dropdown values from the previous
          // category can't leak into the new form and blow up renderers
          // (e.g. a numeric Vintage=2014 reaching a text ComboBox on a
          // different category). Runs for BOTH transitions:
          //   • same master type (France Wine → Italy Wine) — user-added
          //     fields on the old category that don't exist on the new
          //     one are removed. Master fields are shared, so they stay.
          //   • cross-type / non-templated — only the preserved plumbing
          //     survives (matches the previous hard-wipe behaviour).
          // On sameTypeMaster we PRESERVE master + shared user-added
          // keys via the schema-allowed set; on cross-type the schema
          // set + preserved plumbing gives us the same net result as
          // the old hard wipe when there are no shared field names.
          const newSchemaKeys = new Set((data || []).map((f) => f.field_name));
          let prunedThisSwap = 0;
          setValues((prev) => {
            const next = {};
            for (const k of Object.keys(prev)) {
              if (newSchemaKeys.has(k) || preservedKeys.has(k)) {
                next[k] = prev[k];
              } else {
                prunedThisSwap += 1;
              }
            }
            return next;
          });
          // Category Swap Preview — surface the prune count in the UI so
          // the user can see exactly how many stale keys were dropped on
          // this category change. Displayed inline beside the Category
          // dropdown. Cleared automatically on the next swap where
          // nothing had to be pruned.
          setLastPrunedCount(prunedThisSwap);
          // Reference `sameTypeMaster` to keep the same-vs-cross-type
          // signal alive for future callers / audit logs; behaviour is
          // now schema-driven regardless of the value.
          void sameTypeMaster;

          // Spirits category state-leak reset — when the user swaps INTO
          // a Spirits category from anywhere else (Wine, Whisky, non-
          // templated, or a stale Spirits→Spirits within a fresh
          // session), wipe carry-over identity keys so a Wine
          // Producer or a Whisky Distillery never appears pre-filled
          // on a Spirits form. Wine and Whisky flows are untouched;
          // this block only fires on ``newKey === "spirits"``.
          //
          // 2026-02-25 update — preservation rule: Total Bottles and
          // pricing state (Buy Price ex/inc, Market Value ex/inc,
          // Pricing Mode, legacy Buy/Sell Price keys) are RESTORED
          // to survive the swap. These fields are collection-type
          // agnostic — the user just paid for the bottle and typed
          // in a quantity; swapping category shouldn't erase that
          // work. Only Case Size / Case Mode / Units in Case /
          // Case Type / Bottle Count (legacy quantity plumbing that
          // Spirits doesn't use) are still cleared.
          if (newKey === "spirits") {
            setValues((prev) => {
              const next = { ...prev };
              // Legacy quantity plumbing Spirits doesn't consume.
              delete next["Bottle Count"];
              delete next["Case Size"];
              delete next["Case Mode"];
              delete next["Units in Case"];
              delete next["Case Type"];
              // Total Bottles + legacy Buy/Sell Price keys are
              // intentionally preserved — see comment above.
              return next;
            });
          }
        }
        prevCategoryIdRef.current = categoryId;
        // Wine Type defaulting + "Copy Last" — CREATE mode only. Reads the
        // most-recently-saved Wine Type for THIS category from localStorage
        // (cdi.lastWineType.<categoryId>) and falls back to the spec default
        // "Red". Skipped in EDIT mode so we never overwrite the user's
        // existing item. Skipped if the category schema doesn't have a
        // Wine Type field, OR if the user has already typed/selected a
        // value during this session.
        if (!isEdit) {
          const wt = (data || []).find((f) => f.field_name === "Wine Type");
          if (wt) {
            setValues((prev) => {
              if (prev["Wine Type"]) return prev; // user already chose something
              let next = "Red"; // spec default
              try {
                const remembered = window.localStorage.getItem(
                  `cdi.lastWineType.${categoryId}`
                );
                if (remembered) next = remembered;
              } catch {
                /* localStorage disabled — silently fall back to default */
              }
              return { ...prev, "Wine Type": next };
            });
          }
        }
      } catch {
        if (!cancelled) toast.error("Failed to load category fields.");
      } finally {
        if (!cancelled) setLoadingFields(false);
      }
    })();
    return () => { cancelled = true; };
  }, [categoryId]);

  // Whenever the visible field set OR active collection changes, pull the
  // preset values scoped to the current cellar for every text/number field.
  // Each text input becomes a `<datalist>`-backed autocomplete. Skips
  // textarea (free-form prose) and dropdown/multi-select (already have their
  // own option lists). Passing `collection_id` mirrors the fix on
  // /items/field-values so Wine "Region" no longer surfaces Whisky regions
  // and vice versa.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const targets = fields
        .filter((f) => ["text", "number"].includes(f.field_type))
        .map((f) => f.field_name);
      if (targets.length === 0) {
        if (!cancelled) setPresets({});
        return;
      }
      try {
        const qs = encodeURIComponent(targets.join(","));
        const scopeQS = collectionId
          ? `&collection_id=${encodeURIComponent(collectionId)}`
          : "";
        const { data } = await apiClient.get(`/presets?field_names=${qs}${scopeQS}`);
        if (!cancelled) setPresets(data || {});
      } catch {
        if (!cancelled) setPresets({});
      }
    })();
    return () => { cancelled = true; };
  }, [fields, collectionId]);

  // ---------------------------------------------------------------------
  // Structured auto-naming — drives the "Item Name" field on Wine / Whisky /
  // Spirits collections. Composer inputs (Phase B, matching the new
  // Phase A schema):
  //   Wine    : Producer + Appellation + Vintage
  //   Whisky  : Distillery + Age + Cask Type
  //   Spirits : Brand / Producer + Spirit Type
  // The composed value lives in item_data["Item Name"]. `Name Override` is
  // a boolean flag in item_data — set when the user manually edits Item Name
  // so the auto-build stops overwriting their input. Clicking the MANUAL
  // pill (or the empty-field path) clears the flag and rebuilds.
  // ---------------------------------------------------------------------
  const isWineCollection = useMemo(() => {
    const col = collections.find((c) => c.id === collectionId);
    if (!col) return false;
    if (col.type_id === "wine") return true;
    if (!col.type_id && /wine/i.test(col.name || "")) return true;
    return false;
  }, [collections, collectionId]);

  // Whisky gets its own naming track — distinct from Spirits because the
  // composer fields differ (Distillery / Age / Cask Type vs. Brand+Spirit
  // Type). Detection priority: explicit type_id → name keyword.
  const isWhiskyCollection = useMemo(() => {
    const col = collections.find((c) => c.id === collectionId);
    if (!col) return false;
    if (col.type_id === "whisky") return true;
    if (!col.type_id && /whisk(e)?y/i.test(col.name || "")) return true;
    return false;
  }, [collections, collectionId]);

  // Spirits-naming applies to rum, tequila, cognac, gin, vodka, mezcal,
  // armagnac, brandy, and liqueurs. WHISKY is explicitly excluded — it
  // has its own track above.
  const isSpiritsCollection = useMemo(() => {
    const col = collections.find((c) => c.id === collectionId);
    if (!col) return false;
    if (col.type_id === "spirits") return true;
    if (col.type_id === "whisky") return false; // whisky uses isWhiskyCollection
    const n = col.name || "";
    if (/whisk(e)?y/i.test(n)) return false;
    return /(rum|tequila|cognac|gin|vodka|mezcal|armagnac|brandy|liqueur|spirits?)/i.test(n);
  }, [collections, collectionId]);

  // Preset key used by the Unified Category Field UI to classify each
  // field as identity / supporting / legacy. Only the three naming
  // presets (wine, whisky, spirits) have a preset key — every other
  // collection type (general, custom, collectables, ad-hoc) falls
  // back to null so `classifyPresetField` treats every row as optional
  // supporting metadata.
  const collectionTypeId = useMemo(() => {
    if (isWineCollection) return "wine";
    if (isWhiskyCollection) return "whisky";
    if (isSpiritsCollection) return "spirits";
    return null;
  }, [isWineCollection, isWhiskyCollection, isSpiritsCollection]);

  // Wine composer — CDI LOCKED preset order:
  //   Producer → Region (OPTIONAL) → Grape(s) → Vintage → Bottle Size
  // Region auto-skips when blank via filter(Boolean).
  const buildWineName = (producer, region, grape, vintage, bottleSize) => {
    const parts = [producer, region, grape, vintage, bottleSize]
      .map((v) => String(v ?? "").trim())
      .filter(Boolean);
    return parts.join(" ").replace(/\s+/g, " ").trim();
  };

  // Whisky composer — CDI LOCKED order:
  //   Distillery → Whisky Type → Age → Cask Type → Region → ABV → Bottle Size
  const buildWhiskyName = (distillery, whiskyType, age, caskType, region, abv, bottleSize) => {
    const parts = [distillery, whiskyType, age, caskType, region, abv, bottleSize]
      .map((v) => String(v ?? "").trim())
      .filter(Boolean);
    return parts.join(" ").replace(/\s+/g, " ").trim();
  };

  // Spirits composer — CDI LOCKED preset order:
  //   Brand → Spirit Type → Age (OPTIONAL) → Cask Type / Finish →
  //   Region (OPTIONAL) → ABV → Bottle Size
  // Age + Region auto-skip when blank via filter(Boolean).
  const buildSpiritsName = (brand, spiritType, age, caskFinish, region, abv, bottleSize) => {
    const parts = [brand, spiritType, age, caskFinish, region, abv, bottleSize]
      .map((v) => String(v ?? "").trim())
      .filter(Boolean);
    return parts.join(" ").replace(/\s+/g, " ").trim();
  };

  // Wine / Whisky auto-build — DISABLED per Unified Collector Naming
  // directive (2026-02). Item Name is now the Expression field, user-
  // typed on ALL three preset tracks. The AUTO-NAME preview computes
  // the full composed string via `composeAuto(values)` in the render
  // path, so no effect writes back into `values["Item Name"]`.
  // Wine auto-build (2026-02 Wine-only patch) — Item Name auto-fills
  // with the composed AUTO-NAME string whenever an identity field
  // changes AND the user has not set an override. Dependency array
  // is intentionally scoped to the SIX Wine identity keys only
  // (Producer, Region, Grape(s), Vintage, ABV, Bottle Size). Any
  // change to a field in Additional Item Details cannot fire this
  // effect and cannot clear an identity key — details fields write
  // to their own distinct keys via `setFieldValue(f.field_name, v)`.
  //
  // Override / equality guard runs INSIDE the functional updater so
  // it reads fresh `prev` state rather than a closure snapshot —
  // prevents a stale-values race when several identity fields update
  // in the same tick.
  useEffect(() => {
    if (!isWineCollection) return;
    setValues((prev) => {
      // 2026-02-26 Name Override wins — the composer only defers to
      // an explicit `Name Override === true` pin now. Empty Item
      // Name is otherwise happily overwritten on every identity
      // edit. The equality short-circuit below prevents redundant
      // setValues → render loops.
      if ((prev["Item Name"] || "") !== "" && prev["Name Override"] === true) return prev;
      const auto = composeAuto(prev);
      if (!auto || (prev["Item Name"] || "") === auto) return prev;
      return { ...prev, "Item Name": auto };
    });
  }, [
    values["Producer"],
    values["Region"],
    values["Appellation"],
    values["Cuvée"],
    values["Grape(s)"],
    values["Include Grape in Name?"],
    values["Vintage"],
    values["ABV"],
    values["Bottle Size"],
  ]);

  // Whisky auto-build (Phase 5, 2026-02) — mirrors the Wine / Spirits
  // tracks. Item Name auto-fills with the composed AUTO-NAME string
  // whenever a Whisky identity field changes. Dependency array is
  // intentionally scoped to the FIVE Whisky identity keys (Distillery,
  // Whisky Type, Age / Classification, ABV, Bottle Size).
  useEffect(() => {
    if (!isWhiskyCollection) return;
    setValues((prev) => {
      if ((prev["Item Name"] || "") !== "" && prev["Name Override"] === true) return prev;
      const auto = composeAuto(prev);
      if (!auto || (prev["Item Name"] || "") === auto) return prev;
      return { ...prev, "Item Name": auto };
    });
  }, [
    values["Distillery"],
    values["Whisky Type"],
    values["Age / Classification"],
    values["ABV"],
    values["Bottle Size"],
  ]);

  // Spirits auto-build (Phase 2, 2026-02) — mirrors the Wine track.
  // Item Name auto-fills with the composed AUTO-NAME string whenever
  // a Spirits identity field changes. Dependency array is
  // intentionally scoped to the FIVE Spirits identity keys (Brand,
  // Spirit Type, Age Classification, ABV, Bottle Size).
  useEffect(() => {
    if (!isSpiritsCollection) return;
    setValues((prev) => {
      if ((prev["Item Name"] || "") !== "" && prev["Name Override"] === true) return prev;
      const auto = composeAuto(prev);
      if (!auto || (prev["Item Name"] || "") === auto) return prev;
      return { ...prev, "Item Name": auto };
    });
  }, [
    values["Brand"],
    values["Spirit Type"],
    values["Age Classification"],
    values["ABV"],
    values["Bottle Size"],
  ]);

  // Spirits auto-build now runs above (Phase 2, 2026-02) — the legacy
  // no-op anchor has been removed since the Spirits track pins Item
  // Name via a real effect that mirrors the Wine composer.

  // Single composer used by override detection and reset — returns the
  // canonical auto-name for whichever collection-type track is active.
  // Kept in lock-step with the three build*Name helpers above.
  const composeAuto = (src) => {
    // Unified Collector composer (2026-02) — all three preset tracks
    // (Wine / Whisky / Spirits) share the same live-preview behaviour:
    // read the shared library composer using a synthetic collection
    // object so type detection is deterministic (no dependency on the
    // real collection.name regex fallback).
    if (isWineCollection) return composeAutoName(src || {}, { type_id: "wine" });
    if (isWhiskyCollection) return composeAutoName(src || {}, { type_id: "whisky" });
    if (isSpiritsCollection) return composeAutoName(src || {}, { type_id: "spirits" });
    return "";
  };

  const setFieldValue = (name, v) => {
    setValues((prev) => {
      const next = { ...prev, [name]: v };
      // Wine (2026-02 patch — Wine-only): Item Name auto-fills from the
      // composed AUTO-NAME string. Manual edits pin `Name Override`
      // so subsequent identity changes don't clobber them; clearing
      // Item Name lifts the override so the composer takes over again.
      //
      // CRITICAL (2026-02 directive): ANY manual typing into Item Name
      // must pin the override IMMEDIATELY — even when the typed value
      // is identical to the current auto-name. Without this the
      // composer keeps ownership of the field and a later identity
      // edit (Vintage, Region, …) silently overwrites the user's
      // input. The ONLY way to release the override is to clear the
      // field back to empty.
      if (isWineCollection) {
        // 2026-02-26 re-instated manual-override pinning — typing into
        // Item Name pins `Name Override = true` so the three
        // useEffect autobuilders (~L962/989/1009) stop overwriting.
        // Clearing the field drops the override so the effects can
        // repopulate on the next identity-field change.
        if (name === "Item Name") {
          const typed = String(v || "").trim();
          if (!typed) delete next["Name Override"];
          else next["Name Override"] = true;
        }
        return next;
      }
      // Spirits — same decoupled contract as Wine (see comment above).
      if (isSpiritsCollection) {
        if (name === "Item Name") {
          const typed = String(v || "").trim();
          if (!typed) delete next["Name Override"];
          else next["Name Override"] = true;
        }
        return next;
      }
      // Whisky — same decoupled contract as Wine (see comment above).
      if (isWhiskyCollection) {
        if (name === "Item Name") {
          const typed = String(v || "").trim();
          if (!typed) delete next["Name Override"];
          else next["Name Override"] = true;
        }
        return next;
      }
      // Non-Wine/Whisky/Spirits collections — pass-through, no
      // composer wiring. Item Name (when the schema declares it) is
      // treated as a plain user-authored field.
      return next;
    });
  };

  // Reset helper — used by the MANUAL pill. Clears the override flag and
  // immediately rebuilds Item Name from the appropriate composer.
  const resetAutoName = () => {
    setValues((prev) => {
      const next = { ...prev };
      delete next["Name Override"];
      next["Item Name"] = composeAuto(prev);
      return next;
    });
  };
  // Pin-manual helper — previously wired to the MANUAL pill click
  // (removed in Phase 8, 2026-02). Kept as a helper because
  // `setFieldValue` still routes writes through the internal
  // `Name Override` flag; the UI just no longer exposes a manual
  // toggle. Safe to remove once every consumer has migrated.
  const pinManualName = () => {
    setValues((prev) => ({ ...prev, "Name Override": true }));
  };
  // Keep referenced so eslint knows the helper is intentional even
  // though no UI element currently invokes it — the override write
  // path (setFieldValue -> Item Name) is the live consumer.
  void pinManualName;
  // Backwards-compat aliases — old call sites (e.g. lingering tests) still
  // reference the legacy reset names. They now route to the unified helper.
  const resetAutoWineName = resetAutoName;
  const resetAutoSpiritsName = resetAutoName;

  // -------------------------------------------------------------------
  // Item Name autosuggestions — merged & deduped from existing caches:
  //   A. /api/items/names  (distinct existing item names)            → itemNames
  //   B. /api/presets["Item Name"|"Name"] (legacy + imported names) → presets
  //   C. /api/categories (category_name)                             → categories
  //   D. /api/collections (name)                                     → collections
  // The list is gated on a 2-character minimum typed value so the
  // dropdown stays out of the way for empty / single-character input.
  // Sorted alphabetically (case-insensitive); ComboBox handles the live
  // substring filter and caps the visible result count to 20 (maxResults).
  // No new endpoints / API calls — every source is already loaded.
  // -------------------------------------------------------------------
  const nameSuggestions = useMemo(() => {
    const typed = String(values["Item Name"] || "").trim();
    if (typed.length < 2) return [];
    const set = new Set();
    for (const n of itemNames) {
      const t = String(n || "").trim();
      if (t) set.add(t);
    }
    const namePresets = presets["Item Name"] || presets["Name"] || [];
    for (const n of namePresets) {
      const t = String(n || "").trim();
      if (t) set.add(t);
    }
    // Wine-only structured pieces — Producer / Cuvée / Classification
    // values from the presets cache feed the same suggestion list so the
    // user can fast-pick already-typed structured pieces too.
    for (const key of ["Producer", "Cuvée", "Classification"]) {
      for (const n of (presets[key] || [])) {
        const t = String(n || "").trim();
        if (t) set.add(t);
      }
    }
    // Spirits-only structured pieces — Brand / Product / Region values
    // from the presets cache also flow into the suggestion pool so users
    // can pick previously-typed structured spirits pieces.
    for (const key of ["Brand", "Product", "Region"]) {
      for (const n of (presets[key] || [])) {
        const t = String(n || "").trim();
        if (t) set.add(t);
      }
    }
    for (const c of categories) {
      const t = String(c?.category_name || "").trim();
      if (t) set.add(t);
    }
    for (const c of collections) {
      const t = String(c?.name || "").trim();
      if (t) set.add(t);
    }
    const arr = Array.from(set);
    arr.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    return arr;
  }, [values, itemNames, presets, categories, collections]);

  const deriveItemName = () => {
    // 2026-02 persistence rule: user-typed Item Name is authoritative
    // and NEVER replaced by the composed AUTO-NAME on save. The
    // composer only fills in when the field is genuinely blank —
    // whitespace-only strings collapse to blank so a stray space
    // can't pin an accidental name. Legacy field guesses (Name /
    // Title / first text field) remain a last-resort fallback for
    // categories that don't ship an Item Name column.
    const typed = String(values["Item Name"] ?? "").trim();
    if (typed) return typed.slice(0, 120);
    const auto = composeAuto(values);
    if (auto) return String(auto).slice(0, 120);
    const guess = values["Name"] || values["Title"];
    if (guess) return String(guess).slice(0, 120);
    for (const f of fields) {
      if (["text", "textarea"].includes(f.field_type) && values[f.field_name]) {
        return String(values[f.field_name]).slice(0, 120);
      }
    }
    return "Untitled Item";
  };

  const validate = () => {
    const next = {};
    let firstMissing = null;
    // 2026-02-25 Unified Required Fields — the ONLY save-gates are
    // Buy Price (cost_price_ex_gst), Total Bottles, and Category.
    // Item Name is intentionally NOT required at the save layer any
    // more — see below where it is added to `bypassGeneric`. The
    // three useEffect autobuilders keep Item Name populated for
    // Wine / Whisky / Spirits, but a genuinely blank name no longer
    // blocks the save. Spirits still bypasses the legacy
    // "Producer / Distillery" preset which is metadata-only.
    const bypassGeneric = new Set(["Item Name"]);
    if (isSpiritsCollection) {
      bypassGeneric.add("Producer / Distillery");
    }
    for (const f of fields) {
      if (!f.required) continue;
      if (bypassGeneric.has(f.field_name)) continue;
      const v = values[f.field_name];
      const empty = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
      if (empty) {
        next[f.field_name] = `${f.field_name} is required.`;
        if (!firstMissing) firstMissing = f.field_name;
      }
    }
    setFieldErrors(next);
    if (firstMissing) {
      toast.error(`"${firstMissing}" is required.`);
      return false;
    }
    // Unified Identity Gate (2026-02-25 re-instated) — every naming
    // track requires a single, non-empty identity field on BOTH create
    // and edit. Names differ by track but the rule is identical:
    //   Wine    → Producer
    //   Whisky  → Distillery
    //   Spirits → Brand
    // Non-naming collections skip this gate entirely. Item Name is
    // still enforced by the schema-driven generic loop above so both
    // "Name" and "Identity" now block a save.
    if (isWineCollection || isWhiskyCollection || isSpiritsCollection) {
      const identityField = isWineCollection
        ? "Producer"
        : isWhiskyCollection
          ? "Distillery"
          : "Brand";
      const identityValue = String(values[identityField] || "").trim();
      if (!identityValue) {
        setFieldErrors((prev) => ({ ...prev, [identityField]: `${identityField} is required.` }));
        toast.error(`${identityField} is required.`);
        return false;
      }
    }
    // Quantity validation (Model A, 2026-02 hardening): STRICT_QTY_KEY
    // (`Total Bottles`) is a mandatory save-gate on both create and
    // edit paths. Empty / whitespace / non-integer / non-positive
    // values block the save with a field-scoped toast so the user
    // cannot ship an item without stock. `Case Size` remains
    // optional; when set it must be a positive integer too. Full
    // Cases + Loose Bottles are derived read-only. Legacy keys
    // (Bottle Count / Case Mode / Units in Case / Case Type) may
    // still exist on older items but are no longer read or validated.
    const totalBottlesRaw = values[STRICT_QTY_KEY];
    const totalBottlesStr = totalBottlesRaw == null ? "" : String(totalBottlesRaw).trim();
    if (totalBottlesStr === "") {
      setFieldErrors((prev) => ({ ...prev, [STRICT_QTY_KEY]: `${STRICT_QTY_KEY} is required.` }));
      toast.error(`${STRICT_QTY_KEY} is required.`);
      return false;
    }
    const tb = Number(totalBottlesStr);
    if (!Number.isFinite(tb) || tb <= 0 || !Number.isInteger(tb)) {
      setFieldErrors((prev) => ({ ...prev, [STRICT_QTY_KEY]: `${STRICT_QTY_KEY} must be a positive integer.` }));
      toast.error(`${STRICT_QTY_KEY} must be a positive integer.`);
      return false;
    }
    const caseSizeRaw = values["Case Size"];
    if (caseSizeRaw !== "" && caseSizeRaw !== undefined && caseSizeRaw !== null) {
      const cs = Number(caseSizeRaw);
      if (!Number.isFinite(cs) || cs <= 0 || !Number.isInteger(cs)) {
        toast.error("Case Size must be a positive integer.");
        return false;
      }
    }
    // Pricing validation: block negative and non-finite values on any of
    // the four Buy/Sell inputs. Empty strings are permitted (blank price =
    // 0 downstream). Browser `min="0"` on the inputs is a soft hint; this
    // save-time guard is the hard gate so paste / dev-tools / spec-quirk
    // negative values cannot reach the backend.
    const priceInputs = [
      { label: "Buy Price (Ex GST)",  raw: buyEx },
      { label: "Buy Price (Inc GST)", raw: buyInc },
      { label: "Market Value (Ex GST)", raw: marketValueEx },
      { label: "Market Value (Inc GST)",raw: marketValueInc },
    ];
    for (const p of priceInputs) {
      if (p.raw === "" || p.raw === null || p.raw === undefined) continue;
      const n = Number(p.raw);
      if (!Number.isFinite(n)) {
        toast.error(`${p.label} must be a number.`);
        return false;
      }
      if (n < 0) {
        toast.error(`${p.label} cannot be negative.`);
        return false;
      }
    }
    // FX rates: must be positive when the item is foreign-currency.
    // NZD items ignore the FX inputs entirely so their state is not
    // validated here. Zero / negative rates would break NZD-normalised
    // aggregation (divide-by-zero avoidance + negative totals).
    if (String(currencyCode || "NZD").toUpperCase() !== "NZD") {
      const rates = [
        { label: "FX Rate (Purchase)", raw: fxRatePurchase },
        { label: "FX Rate (Sale)",     raw: fxRateSale },
      ];
      for (const r of rates) {
        if (r.raw === "" || r.raw === null || r.raw === undefined) continue;
        const n = Number(r.raw);
        if (!Number.isFinite(n)) {
          toast.error(`${r.label} must be a number.`);
          return false;
        }
        if (n <= 0) {
          toast.error(`${r.label} must be greater than 0.`);
          return false;
        }
      }
    }
    return true;
  };

  // Per-field validator used by the input onBlur handler so required-field
  // errors surface as the user moves off an empty input (not only on
  // submit). Returns the error message (or null) and updates fieldErrors.
  const validateField = (name) => {
    const f = fields.find((x) => x.field_name === name);
    if (!f || !f.required) return null;
    const v = values[name];
    const empty = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
    setFieldErrors((prev) => {
      const copy = { ...prev };
      if (empty) copy[name] = `${name} is required.`;
      else delete copy[name];
      return copy;
    });
    return empty ? `${name} is required.` : null;
  };

  const save = async ({ andNew = false } = {}) => {
    if (!collectionId) { toast.error("Pick a collection first."); return; }
    if (!categoryId) { toast.error("Pick a category first."); return; }
    if (!validate()) return;
    // ------------------------------------------------------------------
    // Strict hardening (2026-02) — spec: quantity / cost / value /
    // category / tag required. Runs the SAME validator as the backend
    // so the two layers can never disagree. 2026-02-25: now runs on
    // BOTH create and edit paths (previous `if (!isEdit)` guard has
    // been removed) so an existing item cannot be edited into an
    // empty-price / empty-quantity state.
    // ------------------------------------------------------------------
    {
      const strictPayload = {
        cost_price_ex_gst: buyEx === "" ? buyInc : buyEx,
        estimated_value_ex_gst: marketValueEx === "" ? marketValueInc : marketValueEx,
        category_id: categoryId,
        item_data: {
          [STRICT_QTY_KEY]: values[STRICT_QTY_KEY],
          [STRICT_TAG_KEY]: values[STRICT_TAG_KEY],
        },
      };
      const strictErrs = validateItemPayload(strictPayload);
      if (Object.keys(strictErrs).length > 0) {
        setFieldErrors((prev) => ({ ...prev, ...mapStrictErrorsToFields(strictErrs) }));
        const first = Object.values(strictErrs)[0];
        toast.error(first || "Please fix the highlighted fields.");
        return;
      }
    }
    setSaving(true);
    try {
      const name = deriveItemName();
      // Save-time precision guard — every money value goes through Decimal.js
      // (via q2) before being JSON-serialised. This catches any sub-cent
      // residue that might exist in state (e.g. a paste of "10.005" that
      // bypassed onBlur, or a third-party autofill). Save is the LAST
      // boundary before the value lands in the database — round here too.
      const toNum2 = (s) => {
        const v = q2(s);
        return v === "" ? 0 : Number(v);
      };
      // Re-derive the auto-calc'd counterpart at save-time too so the two
      // sides are guaranteed in-sync even if some race between an onChange
      // and an onBlur left them out of step. The visible field is the
      // source of truth; the hidden side is recomputed via Decimal math.
      const visBuy  = pricingMode === "ex" ? buyEx  : buyInc;
      const visSell = pricingMode === "ex" ? marketValueEx : marketValueInc;
      const buyExNorm  = pricingMode === "ex" ? q2(visBuy)  : toExFromInc(visBuy);
      const buyIncNorm = pricingMode === "ex" ? toIncFromEx(visBuy) : q2(visBuy);
      const marketValueExNorm = pricingMode === "ex" ? q2(visSell) : toExFromInc(visSell);
      const marketValueIncNorm= pricingMode === "ex" ? toIncFromEx(visSell): q2(visSell);
      // Unified pricing payload — write all 4 canonical GST-split fields, plus
      // keep legacy `purchase_price` / `estimated_value` in sync (mirrored
      // from Buy Inc / Sell Ex) so any legacy reader still sees consistent
      // numbers across the app. Every value is the Decimal-quantised "X.YY".
      const pricingPayload = {
        cost_price_ex_gst:        toNum2(buyExNorm),
        cost_price_inc_gst:       toNum2(buyIncNorm),
        estimated_value_ex_gst:   toNum2(marketValueExNorm),
        estimated_value_inc_gst:  toNum2(marketValueIncNorm),
        // resolveValuation() spec — new persisted fields. Market value
        // mirrors the existing marketValueEx input; manual + source
        // come from their own controls. The legacy
        // `estimated_value_ex_gst` above is retained for backwards-
        // compat analytics but is IGNORED by the new resolver.
        market_valuation_ex_gst:  marketValueExNorm === "" ? null : toNum2(marketValueExNorm),
        manual_valuation_ex_gst:  manualValueEx === "" ? null : toNum2(manualValueEx),
        valuation_source:         valuationSource || null,
        purchase_price:           toNum2(buyIncNorm),  // legacy mirror = Buy Inc
        estimated_value:          toNum2(marketValueExNorm),  // legacy mirror = Sell Ex
      };
      // Compose the FX-rates portion of the payload once. An empty
      // input (user never touched the field) defaults to 1.0 which is
      // a no-op multiplier. A user-typed 0 is preserved verbatim —
      // aggregation helpers (backend + itemValueNZD) treat sale=0 as
      // "not set" and fall back to the purchase rate for the NZD-value
      // calculation, so the collection total stays stable.
      const fxPayload = {
        fx_rate_purchase: fxRatePurchase === "" ? 1 : Number(fxRatePurchase),
        fx_rate_sale:     fxRateSale     === "" ? 1 : Number(fxRateSale),
      };
      // Unified Case Math save-mapping (Fix #8 Part 1).
      //
      // Rules — applied identically to Wine, Whisky, and Spirits so
      // every collection persists the same shape:
      //
      //   • When Total Bottles = N and Case Size C > 1 is set:
      //       Case Mode       = "Case"
      //       Case Size       = C
      //       Total Bottles   = N
      //       Bottle Count    = N
      //       Number of Cases = floor(N / C)
      //       Loose Bottles   = N mod C
      //
      //   • When Case Size is empty / null / 0 / 1:
      //       Case Mode       = "Single Bottle"
      //       Case Size       = null (dropped from payload)
      //       Total Bottles   = N
      //       Bottle Count    = N
      //       Number of Cases = 0
      //       Loose Bottles   = N   (every bottle is loose in single-bottle mode)
      //
      // Both Total Bottles and Bottle Count are stamped with the same
      // value — Bottle Count is the display-name Item Details uses;
      // Total Bottles is the legacy analytics field. Legacy items that
      // only had one key round-trip cleanly. Number of Cases + Loose
      // Bottles are derived from the UI math and persisted (2026-02
      // save-path fix) so ItemDetail reads back the exact numbers the
      // user saw during entry.
      //
      // If Total Bottles is unset, the block is a no-op — the save
      // preserves whatever the item already had, so opening + saving a
      // legacy record without touching Quantity doesn't erase data.
      //
      // NOTE: Analytics (Dashboard total-bottles KPI, collection
      // totals, profit/loss) reads item-level records — this block only
      // affects the shape of the item_data payload, not those totals.
      const buildItemData = () => {
        const base = { ...values, _pricing_mode: pricingMode };

        const rawTotal = base["Total Bottles"];
        const totalNum = Number(rawTotal);
        const hasTotal = rawTotal !== undefined && rawTotal !== "" && rawTotal !== null
          && Number.isFinite(totalNum) && totalNum >= 0;

        if (!hasTotal) {
          return base;
        }

        const rawCase = base["Case Size"];
        const caseNum = Number(rawCase);
        const hasCase = rawCase !== undefined && rawCase !== "" && rawCase !== null
          && Number.isFinite(caseNum) && caseNum > 1;

        if (hasCase) {
          base["Case Mode"]       = "Case";
          base["Case Size"]       = caseNum;
          base["Number of Cases"] = Math.floor(totalNum / caseNum);
          base["Loose Bottles"]   = totalNum % caseNum;
        } else {
          base["Case Mode"]       = "Single Bottle";
          base["Case Size"]       = 1;
          base["Number of Cases"] = 0;
          base["Loose Bottles"]   = totalNum;
        }
        base["Total Bottles"] = totalNum;
        base["Bottle Count"]  = totalNum;

        return base;
      };
      const itemDataForSave = buildItemData();
      // Quantity migration Step 2 (2026-02-26) — mirror the derived
      // quantity numbers into the new top-level columns so the
      // backend can serve reads directly from them. item_data is
      // preserved unchanged as the fallback source (Step 1 validator
      // still backfills these fields on legacy items).
      const _qtyNum = (v) => {
        if (v === null || v === undefined || v === "") return null;
        const n = Number(v);
        return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
      };
      // Quantity Migration Step 5 (2026-02-26) — total_units is
      // derived from the case-math formula, NOT copied from the
      // free-text "Total Bottles" field. This keeps client + server
      // agreed on `(cases or 0) * (case_size or 1) + (loose or 0)`
      // regardless of what the user typed into a mirrored dynamic
      // field.
      const _caseSize = _qtyNum(itemDataForSave["Case Size"]);
      const _cases    = _qtyNum(itemDataForSave["Number of Cases"]);
      const _loose    = _qtyNum(itemDataForSave["Loose Bottles"]);
      const quantityColumns = {
        case_size:   _caseSize,
        cases:       _cases,
        loose_units: _loose,
        total_units: (_cases || 0) * (_caseSize || 1) + (_loose || 0),
      };
      // TASK 2 (2026-02-22): tax_name must reflect the item's currency at
      // save time so downstream wording (P&L, "Entered as", pricing rows)
      // renders correctly. Fixed mapping — no math is affected.
      const _resolveTaxName = (ccy) => {
        switch (String(ccy || "").toUpperCase()) {
          case "USD": return "Sales Tax";
          case "NZD": return "GST";
          case "GBP":
          case "EUR": return "VAT";
          default: return "Tax";
        }
      };
      const _resolvedTaxName = _resolveTaxName(currencyCode);
      if (isEdit) {
        const body = {
          name,
          item_name: name,
          category_id: categoryId,
          collection_id: collectionId || null,
          item_data: itemDataForSave,
          ...quantityColumns,
          ...pricingPayload,
          currency_code: currencyCode || "NZD",
          tax_name: _resolvedTaxName,
          ...fxPayload,
        };
        if (imageDirty) body.image = image ? image : null;
        if (imagesDirty) body.images = images;
        await apiClient.patch(`/items/${editId}`, body);
        toast.success("Item updated.");
        // TASK: route Item Details back to the Category Item List inside
        // the current Collection. Pass state.from as a hint so the back
        // resolver has the originating list URL even before item.* is
        // hydrated.
        const editFrom = collectionId && categoryId
          ? `/items?collection_id=${collectionId}&category_id=${categoryId}`
          : (collectionId ? `/collections/${collectionId}` : "/items");
        navigate(`/items/${editId}`, { state: { from: editFrom } });
      } else {
        const createdRes = await apiClient.post("/items", {
          name,
          item_name: name,
          category_id: categoryId,
          collection_id: collectionId || null,
          item_data: itemDataForSave,
          ...quantityColumns,
          ...pricingPayload,
          currency_code: currencyCode || "NZD",
          tax_name: _resolvedTaxName,
          ...fxPayload,
          image: image || "",      // first image becomes the primary thumbnail
          images: images,          // additional photos (back label, etc.)
        });
        const newItemId = createdRes?.data?.id;
        // Remember the last-used category for the next rapid-entry session.
        try { localStorage.setItem("cdi.lastCategoryId", categoryId); } catch (_e) { /* noop */ }
        // "Copy Last" — persist the just-saved Wine Type per category so the
        // next Create-Item screen for the SAME category pre-fills with it.
        // Speeds up multi-vintage entry where the user logs many bottles of
        // the same colour/style in a row. Falls back silently if storage
        // isn't available (e.g. private window).
        try {
          const wt = values["Wine Type"];
          if (wt) localStorage.setItem(`cdi.lastWineType.${categoryId}`, String(wt));
        } catch (_e) { /* noop */ }
        toast.success(andNew ? "Item saved. Add another." : "Item saved.");
        // Rehydrate the dropdown/chip pools so newly-typed values
        // (Producer, Cuvée, Region, Sub-Region, Grape, Bottle Size, …)
        // appear in the autosuggest immediately — without needing a
        // remount. Runs in the background; failures fall through silently
        // so a flaky network never blocks the save UX. Scoped to the
        // saved item's collection so Wine values never leak into Whisky.
        const saveScopeQS = collectionId
          ? `&collection_id=${encodeURIComponent(collectionId)}`
          : "";
        Promise.all(
          SUGGEST_FIELDS.map((f) =>
            apiClient.get(`/items/field-values?field=${encodeURIComponent(f)}${saveScopeQS}`)
              .catch(() => ({ data: { values: [] } }))
          )
        ).then((results) => {
          setFieldValues((prev) => {
            const next = { ...prev };
            SUGGEST_FIELDS.forEach((f, i) => {
              next[f] = results[i]?.data?.values || [];
            });
            return next;
          });
          // Save committed → the server's view of fieldValues is now the
          // source of truth. Any value still flagged as session-only is
          // either now persisted (server returned it in the fetch above)
          // or was never saved (filtered out). Either way the ★ marker
          // is no longer accurate, so clear the whole optimistic set.
          setOptimisticValues({});
        });
        if (andNew) {
          // Reset only the per-item fields; keep collection + category sticky.
          // Wine Type stays sticky too — "Copy Last" carries the just-saved
          // value into the next entry so the user can log a string of bottles
          // of the same colour without re-picking.
          const stickyWineType = values["Wine Type"];
          setValues(stickyWineType ? { "Wine Type": stickyWineType } : {});
          setPurchasePrice("");
          setEstimatedValue("");
          setBuyEx(""); setBuyInc(""); setMarketValueEx(""); setMarketValueInc("");
          setImage("");
          setImages([]);
          setImageDirty(false);
          setImagesDirty(false);
          // Focus the first dynamic field for instant typing.
          setTimeout(() => {
            const first = document.querySelector('[data-testid^="ie-field-"]');
            if (first && typeof first.focus === "function") first.focus();
          }, 50);
        } else {
          // TASK: after creating a new item, route the user to the
          // Item Details page (parity with Edit save). The Back button
          // there resolves to the Category Item List inside the current
          // Collection. Falls back to the collection list when the
          // create response doesn't return an id (defensive).
          if (newItemId) {
            const from = collectionId && categoryId
              ? `/items?collection_id=${collectionId}&category_id=${categoryId}`
              : (collectionId ? `/collections/${collectionId}` : "/items");
            navigate(`/items/${newItemId}`, { state: { from } });
          } else {
            navigate(collectionId ? `/collections/${collectionId}` : "/items");
          }
        }
      }
    } catch (e) {
      // Strict-validation 422 responses carry `{ detail: { errors: {..}, message } }`.
      // Surface every field-level message on the form + toast the first one.
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.errors && typeof detail.errors === "object") {
        const mapped = mapStrictErrorsToFields(detail.errors);
        setFieldErrors((prev) => ({ ...prev, ...mapped }));
        const first = Object.values(detail.errors)[0];
        toast.error(first || detail.message || "Please fix the highlighted fields.");
      } else {
        const raw = detail;
        const msg = Array.isArray(raw)
          ? raw.map((er) => `${(er.loc || []).slice(-1)[0] || "field"}: ${er.msg}`).join("; ")
          : (typeof raw === "string" ? raw : "Failed to save.");
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  // Keyboard shortcuts:
  //   Enter (inside text/number/date input) → focus next focusable field
  //   Cmd/Ctrl+Enter                        → Save & New (create mode only)
  //   Esc                                   → Cancel (back to /items)
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      // Task C-Fix: ESC returns to the collection context if we have one,
      // matching the post-save redirect.
      navigate(collectionId ? `/collections/${collectionId}` : "/items");
      return;
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (!isEdit) save({ andNew: true });
      else save();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      const t = e.target;
      const tag = (t.tagName || "").toLowerCase();
      const type = (t.type || "").toLowerCase();
      // Skip textareas (Enter is newline) and buttons/selects.
      if (tag !== "input" || ["checkbox", "radio", "button", "submit"].includes(type)) return;
      e.preventDefault();
      // Find the next focusable input/select/textarea in tab order.
      const root = e.currentTarget;
      const focusables = Array.from(root.querySelectorAll(
        "input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [contenteditable=true]"
      ));
      const idx = focusables.indexOf(t);
      if (idx >= 0 && idx + 1 < focusables.length) {
        const next = focusables[idx + 1];
        next.focus();
        if (next.select && next.type !== "checkbox") next.select();
      } else {
        // No more fields — submit as Save (or do nothing in edit if user prefers).
        save();
      }
    }
  };

  return (
    <div
      className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14"
      data-testid="item-entry"
      onKeyDown={handleKeyDown}
    >
      {/* Back — explicit parent route: always /collections/{collectionId}.
          No history/tracker consulted. Falls back to /items only when no
          collectionId is known (e.g. create-mode deep link with no
          preselected collection). */}
      <button
        type="button"
        onClick={() => navigate(`/collections/${collectionId}`)}
        data-testid="ie-back"
        aria-label="Back"
        className="vp-btn vp-btn-secondary vp-btn-sm mb-3 inline-flex items-center gap-2"
      >
        <ArrowLeft size={14} />
        <span>Back</span>
      </button>

      {/* Breadcrumb: [Collection] (or Home) → Items / [Item Name | New Item] */}
      {(() => {
        const col = collections.find((c) => c.id === collectionId);
        const itemTitle = isEdit
          ? (values["Item Name"] || "Untitled")
          : "New Item";
        return (
          <nav
            aria-label="Breadcrumb"
            data-testid="ie-breadcrumb"
            className="flex items-center gap-1.5 flex-wrap text-xs mb-6"
          >
            <Link
              to="/"
              data-testid="ie-breadcrumb-home"
              className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast"
            >
              Home
            </Link>
            {col && <span className="vp-muted">→</span>}
            {col && (
              <Link
                to={`/collections/${col.id}`}
                data-testid="ie-breadcrumb-collection"
                title="Open collection"
                className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast"
              >
                {col.name}
              </Link>
            )}
            <span className="vp-muted">→</span>
            <Link
              to="/items"
              data-testid="ie-breadcrumb-items"
              className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast"
            >
              Items
            </Link>
            <span className="vp-muted">/</span>
            <span
              data-testid="ie-breadcrumb-current"
              className="text-xs font-medium truncate max-w-[40ch]"
              title={itemTitle}
            >
              {itemTitle}
            </span>
          </nav>
        );
      })()}

      <div className="vp-label mb-2">Item Entry</div>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-10">
        {isEdit ? "Edit item" : "Create a new item"}
      </h1>

      {/* Images — combined primary + secondary into a single multi-image
          uploader (no fixed cap). First image is the primary thumbnail
          shown on the Items list. Shown in BOTH create and edit modes.
          Reorder controls (◀ ▶) let users promote any image to primary
          or rearrange the gallery; delete (X) removes a single image and
          re-indexes the rest (the first remaining image becomes primary). */}
      {(() => {
        // Fix #12 — Photo Limits.
        // ``isPremium`` is hardcoded false until auth lands (mirrors
        // the Dashboard convention). Basic caps at 3 photos, Premium
        // at 50. When user auth arrives, this constant becomes
        // ``currentUser.plan === "premium"``.
        const isPremium = !!user?.premium_plan;
        const MAX_IMAGES = isPremium ? 50 : 3;
        const PLAN_MSG = isPremium
          ? "Premium plan allows up to 50 photos."
          : "Basic plan allows up to 3 photos.";
        // The full ordered list we manage in this UI = [primary, ...secondaries].
        const allImages = [image, ...images].filter(Boolean);
        const canAddMore = allImages.length < MAX_IMAGES;
        const commitOrdered = (next) => {
          // First entry becomes the new primary, rest become `images[]`.
          const cleaned = next.filter(Boolean).slice(0, MAX_IMAGES);
          setImage(cleaned[0] || "");
          setImages(cleaned.slice(1));
          setImageDirty(true);
          setImagesDirty(true);
        };
        const moveAt = (idx, delta) => {
          const target = idx + delta;
          if (target < 0 || target >= allImages.length) return;
          const next = allImages.slice();
          [next[idx], next[target]] = [next[target], next[idx]];
          commitOrdered(next);
        };
        const handleFiles = (fileList) => {
          const files = Array.from(fileList || []);
          if (!files.length) return;
          // Strict file-type gate — only JPG / PNG / WEBP allowed. Matches
          // the backend allowlist in `create_item()` / `update_item()`.
          const ALLOWED_MIMES = new Set(["image/jpeg", "image/png", "image/webp"]);
          const ALLOWED_EXTS  = /\.(jpe?g|png|webp)$/i;
          const isAllowed = (f) =>
            (f.type && ALLOWED_MIMES.has(f.type.toLowerCase())) ||
            (f.name && ALLOWED_EXTS.test(f.name));
          const valid = files.filter(isAllowed);
          if (valid.length !== files.length) {
            setImageTypeError("Only JPG, PNG, or WEBP images are allowed.");
          } else {
            setImageTypeError("");
          }
          if (!valid.length) return;
          const room = MAX_IMAGES - allImages.length;
          if (room <= 0) { toast.error(PLAN_MSG); return; }
          const take = valid.slice(0, room);
          if (valid.length > room) toast(`Added ${room} image${room === 1 ? "" : "s"}; ${PLAN_MSG}`, { icon: "📷" });
        const readers = take.map((f) =>
            new Promise((resolve, reject) => {
              if (f.size > 4 * 1024 * 1024) {
                toast.error(`"${f.name}" is larger than 4 MB.`);
                resolve(null);
                return;
              }
              const r = new FileReader();
              r.onload = () => resolve(r.result || null);
              r.onerror = reject;
              r.readAsDataURL(f);
            })
          );
          Promise.all(readers).then((results) => {
            const fresh = results.filter(Boolean);
            if (fresh.length) commitOrdered([...allImages, ...fresh]);
          });
        };
        const removeAt = (idx) => commitOrdered(allImages.filter((_, i) => i !== idx));
        return (
          <section className="vp-card p-4 mb-6 vp-fade-in" data-testid="ie-image-section">
            <div className="flex items-baseline justify-between mb-2">
              <label className="block text-xs font-medium vp-muted">
                Photos
              </label>
              <span className="text-[10px] uppercase tracking-wider vp-muted">
                {allImages.length} / {MAX_IMAGES}
              </span>
            </div>
            {imageTypeError && (
              <p data-testid="ie-image-type-error" className="text-[11px] text-[hsl(var(--loss))] mb-2">
                {imageTypeError}
              </p>
            )}
            <div className="flex flex-wrap gap-2 mb-3" data-testid="ie-image-thumbs">
              {allImages.map((src, idx) => (
                <div
                  key={idx}
                  className="relative w-24 h-24 rounded-md overflow-hidden border border-[hsl(var(--border))] bg-[hsl(var(--muted))]"
                  data-testid={`ie-image-thumb-${idx}`}
                >
                  <img src={src} alt={`Image ${idx + 1}`} className="w-full h-full object-cover" />
                  {idx === 0 && (
                    <span className="absolute top-1 left-1 text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-black/60 text-white">
                      Primary
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    data-testid={`ie-image-remove-${idx}`}
                    aria-label={`Remove image ${idx + 1}`}
                    className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white hover:bg-[hsl(var(--loss))] transition-colors"
                  >
                    <X size={11} />
                  </button>
                  {/* Reorder controls — promote earlier or push later. The
                      left arrow on idx 1 promotes a secondary to primary.
                      Buttons disable themselves at the ends of the list. */}
                  <div className="absolute bottom-1 left-1 right-1 flex justify-between gap-1">
                    <button
                      type="button"
                      onClick={() => moveAt(idx, -1)}
                      disabled={idx === 0}
                      data-testid={`ie-image-move-left-${idx}`}
                      aria-label={`Move image ${idx + 1} earlier`}
                      title="Move earlier"
                      className="p-0.5 rounded bg-black/60 text-white hover:bg-black/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={11} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveAt(idx, 1)}
                      disabled={idx === allImages.length - 1}
                      data-testid={`ie-image-move-right-${idx}`}
                      aria-label={`Move image ${idx + 1} later`}
                      title="Move later"
                      className="p-0.5 rounded bg-black/60 text-white hover:bg-black/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight size={11} />
                    </button>
                  </div>
                </div>
              ))}
              {canAddMore && (
                <label
                  className="w-24 h-24 rounded-md border border-dashed border-[hsl(var(--border))] flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-[hsl(var(--muted))] vp-muted"
                  data-testid="ie-image-add"
                >
                  <Upload size={16} />
                  <span className="text-[10px]">{allImages.length === 0 ? "Upload" : "Add more"}</span>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    data-testid="ie-image-input"
                    onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
                  />
                </label>
              )}
            </div>
            <p className="text-[10px] vp-muted">
              Up to {MAX_IMAGES} photos per item (front label, back label, vintage stamp…). First image is used as the list thumbnail. Use the arrows to reorder. Max 4 MB each.
            </p>
          </section>
        );
      })()}

      {/* Collection select — explicit Item↔Collection link */}
      <section className="vp-card p-6 mb-6">
        <label htmlFor="ie-collection" className="block text-xs font-medium mb-1.5 vp-muted">
          Collection <span className="text-[hsl(var(--loss))]">*</span>
        </label>
        <select
          id="ie-collection"
          data-testid="ie-collection"
          value={collectionId}
          onChange={(e) => {
            const newCol = e.target.value;
            setCollectionId(newCol);
            // If the currently-picked category doesn't belong to the new
            // collection, clear it so the user has to pick a valid one.
            // We DO NOT wipe `values` here — the schema-load useEffect
            // (see [categoryId] deps) applies the Phase C preservedKeys +
            // allowedNames filter once the new category's fields arrive,
            // so Item Name / Bottle Size / Notes / Case Mode / Bottle
            // Count / Units in Case / Case Type / Name Override survive
            // the switch.
            const cat = categories.find((c) => c.id === categoryId);
            if (cat && cat.collection_id && cat.collection_id !== newCol) {
              setCategoryId("");
            }

            // Edit-mode rehydration: if the user has wandered off to a
            // foreign collection type (e.g. Wine → Whisky) — which
            // triggered the cross-type hard wipe — and is now returning
            // to the item's ORIGINAL collection, restore everything from
            // the snapshot we took at load time. Fires BEFORE the user
            // picks a category, so all Wine fields (Producer, Vintage,
            // Appellation, Region, Grape(s), Bottle Size, Pricing,
            // Quantity, Images, etc.) reappear immediately on selecting
            // the home collection again. Snapshot is only present in
            // Edit mode; create-mode users are unaffected.
            const snap = editSnapshotRef.current;
            if (snap && newCol === snap.collectionId) {
              // Pre-sync prevCategoryIdRef so the schema-load useEffect
              // treats the restored category as "unchanged" and skips
              // the cross-type wipe that would otherwise nuke the values
              // we're about to restore.
              prevCategoryIdRef.current = snap.categoryId;
              setValues({ ...snap.values });
              setCategoryId(snap.categoryId);
              setImage(snap.image);
              setImages(snap.images);
              setBuyEx(snap.buyEx);
              setBuyInc(snap.buyInc);
              setMarketValueEx(snap.marketValueEx);
              setMarketValueInc(snap.marketValueInc);
              setCurrencyCode(snap.currencyCode);
              setPricingMode(snap.pricingMode);
              setPurchasePrice(snap.purchasePrice);
              setEstimatedValue(snap.estimatedValue);
            }
          }}
          className="vp-select vp-select-strong w-full"
        >
          <option value="">— Select a collection —</option>
          {collections.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {collections.length === 0 && (
          <p className="text-xs vp-muted mt-2">
            No collections yet.{" "}
            <Link to="/collections" className="underline text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]">
              Create one first
            </Link>.
          </p>
        )}
      </section>

      {/* Category select */}
      <section className="vp-card p-6 mb-6">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <label htmlFor="ie-category" className="block text-xs font-medium vp-muted">
            Category <span className="text-[hsl(var(--loss))]">*</span>
          </label>
          {categoryId && !isEdit && (
            <Link
              to={`/categories/${categoryId}/edit?next=${encodeURIComponent(location.pathname + location.search)}`}
              data-testid="ie-manage-fields"
              className="text-[11px] font-medium text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] hover:underline inline-flex items-center gap-1"
              title="Open Category Builder to add or edit fields for this category"
            >
              <Edit2 size={11} /> Manage fields
            </Link>
          )}
        </div>
        <select
          id="ie-category"
          data-testid="ie-category"
          value={categoryId}
          onChange={(e) => {
            const newCat = e.target.value;
            // No wipe here — the schema-load useEffect (deps: [categoryId])
            // runs the Phase C preservedKeys + allowedNames filter as soon
            // as the new category's fields arrive, so we don't need to
            // pre-wipe. Wiping here previously killed Item Name / Bottle
            // Size / Notes / Case Mode / Bottle Count / Units in Case /
            // Case Type / Name Override on every switch.
            setCategoryId(newCat);
            const cat = categories.find((c) => c.id === newCat);
            if (cat && cat.collection_id) setCollectionId(cat.collection_id);
          }}
          className="vp-select vp-select-strong w-full"
        >
          <option value="">— Select a category —</option>
          {categories
            .filter((c) => !collectionId || c.collection_id === collectionId)
            .map((c) => (
              <option key={c.id} value={c.id}>{c.category_name}</option>
            ))}
        </select>
        {/* Category Swap Preview — informational only. Shows how many
            stale value keys were dropped by the most-recent prune-on-
            category-swap pass. Never blocks the category change; only
            visible when count > 0 so the row stays quiet on same-schema
            transitions. */}
        {lastPrunedCount > 0 ? (
          <p
            data-testid="ie-category-swap-preview"
            aria-live="polite"
            className="mt-1 text-[11px] italic text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
          >
            {lastPrunedCount} field{lastPrunedCount === 1 ? "" : "s"} will be cleared
          </p>
        ) : null}
        {/* Helper text — Wine / Whisky / Spirits templates only. UI-only
            hint to demystify what users can put in the Category name.
            Category field type, schema and behaviour are unchanged. */}
        {(() => {
          const col = collections.find((c) => c.id === collectionId);
          const name = String(col?.name || "");
          let helper = null;
          if (isWineCollection) {
            helper = "Name this category anything you like — e.g., France, Italy, Red Wine, Champagne, NZ, Premium.";
          } else if (isSpiritsCollection) {
            const isWhisky = col?.type_id === "whisky" || /whisky|whiskey/i.test(name);
            helper = isWhisky
              ? "Name this category anything you like — e.g., Speyside, Islay, Japan, Ireland, Single Malt, Premium."
              : "Name this category anything you like — e.g., Rum, Gin, Tequila, Cognac, Premium, Limited Editions.";
          }
          return helper ? (
            <p data-testid="ie-category-helper" className="text-[11px] vp-muted mt-1.5">
              {helper}
            </p>
          ) : null;
        })()}
        {categories.filter((c) => !collectionId || c.collection_id === collectionId).length === 0 && (
          <div
            data-testid="ie-no-categories-empty-state"
            className="mt-3 rounded-md border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--accent))] p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">
                {collectionId ? "No categories in this collection yet" : "No categories yet"}
              </p>
              <p className="text-xs vp-muted mt-0.5">
                You need at least one category before adding items. Create one now and come back here.
              </p>
            </div>
            <Link
              to={collectionId ? `/categories?collection_id=${collectionId}&return_to=${encodeURIComponent("/items/new")}` : `/categories?return_to=${encodeURIComponent("/items/new")}`}
              data-testid="ie-no-categories-create-btn"
              className="vp-btn vp-btn-sm vp-btn-gold shrink-0"
            >
              <Plus size={14} />
              <span className="text-xs font-medium">Create a category</span>
            </Link>
          </div>
        )}
      </section>

      {/* Pricing & Currency — unified Buy/Sell × Ex/Inc GST grid backed by
          cost_price_ex_gst / cost_price_inc_gst / estimated_value_ex_gst /
          estimated_value_inc_gst on the Item model. Same fields are read by
          Item Detail and the Dashboard helpers (itemCost / itemValue) so all
          surfaces stay in sync. */}
      <section className="vp-card p-6 mb-6" data-testid="ie-pricing-section">
        <h2 className="text-sm font-semibold mb-4">Pricing</h2>

        {/* Valuation Source selector (resolveValuation spec, 2026-02).
            Drives which input feeds the read-side Sell / Valuation:
              • Market   → market_valuation_ex_gst  (uses the Market
                Value inputs below)
              • Manual   → manual_valuation_ex_gst  (uses the Manual
                Valuation input below)
              • Computed → weighted average of all sales (server-side)
              • blank    → Computed default, falls back to Buy Price
                when no sales exist. */}
        <div className="mb-5" data-testid="ie-valuation-source-block">
          <label className="vp-label block mb-1">Valuation Source</label>
          <select
            data-testid="ie-valuation-source"
            className="vp-input w-full"
            value={valuationSource}
            onChange={(e) => setValuationSource(e.target.value)}
          >
            <option value="">Buy Price (Fallback)</option>
            <option value="Market">Market</option>
            <option value="Manual">Manual</option>
            <option value="Computed">Sale Price (Auto)</option>
          </select>
          {valuationSource === "Manual" && (
            <div className="mt-3">
              <label className="vp-label block mb-1">Manual Valuation (Ex GST)</label>
              <input
                data-testid="ie-manual-valuation-ex"
                className="vp-input w-full"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={manualValueEx}
                onChange={(e) => setManualValueEx(e.target.value)}
                onBlur={(e) => setManualValueEx(q2(e.target.value))}
                placeholder="0.00"
              />
            </div>
          )}
        </div>
        {/* Per-item GST mode toggle. Drives which input variants are
            visible below; the hidden variants are still kept in state so
            the canonical 4-field payload (cost_price_ex_gst /
            cost_price_inc_gst / estimated_value_ex_gst /
            estimated_value_inc_gst) saves unchanged. */}
        <fieldset
          className="mb-5 border border-[hsl(var(--border))] rounded-lg p-3"
          data-testid="ie-pricing-mode"
          role="radiogroup"
          aria-label="How are you entering prices for this item?"
        >
          <legend className="px-2 text-[10px] font-semibold uppercase tracking-wider vp-muted">
            How are you entering prices for this item?
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
            {[
              { v: "inc", label: `Price includes ${taxLabel}`, testid: "ie-pricing-mode-inc" },
              { v: "ex",  label: `Price excludes ${taxLabel}`, testid: "ie-pricing-mode-ex"  },
            ].map(({ v, label, testid }) => {
              const active = pricingMode === v;
              return (
                <label
                  key={v}
                  data-testid={testid}
                  className={`flex items-center gap-2 cursor-pointer rounded-md border px-3 py-2 transition-colors duration-fast ${
                    active
                      ? "border-[hsl(var(--gold))] bg-[hsl(var(--accent))]"
                      : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
                  }`}
                >
                  <input
                    type="radio"
                    name="ie-pricing-mode"
                    value={v}
                    checked={active}
                    onChange={() => onPricingModeChange(v)}
                    className="accent-[hsl(var(--gold))]"
                    data-testid={`${testid}-input`}
                  />
                  <span className={`text-sm ${active ? "font-medium" : ""}`}>{label}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* --- Buy --- */}
          <div className="space-y-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--text))]">
              Buy Price
            </div>
            {pricingMode === "ex" ? (
              <div>
                <label htmlFor="ie-buy-ex" className="block text-xs font-medium mb-1.5 vp-muted">
                  Buy Price <span className="text-[hsl(var(--gold-strong))]">(Ex {taxLabel})</span> <span className="text-[hsl(var(--gold-strong))]" data-testid="ie-buy-ex-currency">— {currencyCode}</span>
                </label>
                <input
                  id="ie-buy-ex"
                  data-testid="ie-buy-price-ex"
                  type="number" step="0.01" min="0" inputMode="decimal" placeholder="0.00"
                  value={buyEx}
                  onChange={(e) => onBuyTyped(e.target.value)}
                  onBlur={onBuyBlur}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="vp-input w-full"
                />
              </div>
            ) : (
              <div>
                <label htmlFor="ie-buy-inc" className="block text-xs font-medium mb-1.5 vp-muted">
                  Buy Price <span className="text-[hsl(var(--gold-strong))]">(Inc {taxLabel})</span> <span className="text-[hsl(var(--gold-strong))]" data-testid="ie-buy-inc-currency">— {currencyCode}</span>
                </label>
                <input
                  id="ie-buy-inc"
                  data-testid="ie-buy-price-inc"
                  type="number" step="0.01" min="0" inputMode="decimal" placeholder="0.00"
                  value={buyInc}
                  onChange={(e) => onBuyTyped(e.target.value)}
                  onBlur={onBuyBlur}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="vp-input w-full"
                />
              </div>
            )}
          </div>
          {/* --- Sell --- */}
          <div className="space-y-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--text))]">
              Market Value
            </div>
            {pricingMode === "ex" ? (
              <div>
                <label htmlFor="ie-sell-ex" className="block text-xs font-medium mb-1.5 vp-muted">
                  Market Value <span className="text-[hsl(var(--gold-strong))]">(Ex {taxLabel})</span> <span className="text-[hsl(var(--gold-strong))]" data-testid="ie-sell-ex-currency">— {currencyCode}</span>
                </label>
                <input
                  id="ie-sell-ex"
                  data-testid="ie-sell-price-ex"
                  type="number" step="0.01" min="0" inputMode="decimal" placeholder="0.00"
                  value={marketValueEx}
                  onChange={(e) => onSellTyped(e.target.value)}
                  onBlur={onSellBlur}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="vp-input w-full"
                />
              </div>
            ) : (
              <div>
                <label htmlFor="ie-sell-inc" className="block text-xs font-medium mb-1.5 vp-muted">
                  Market Value <span className="text-[hsl(var(--gold-strong))]">(Inc {taxLabel})</span> <span className="text-[hsl(var(--gold-strong))]" data-testid="ie-sell-inc-currency">— {currencyCode}</span>
                </label>
                <input
                  id="ie-sell-inc"
                  data-testid="ie-sell-price-inc"
                  type="number" step="0.01" min="0" inputMode="decimal" placeholder="0.00"
                  value={marketValueInc}
                  onChange={(e) => onSellTyped(e.target.value)}
                  onBlur={onSellBlur}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="vp-input w-full"
                />
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 items-end">
          {/* Step 2 (2026-02) — the per-item Currency dropdown has been
              REMOVED from the Pricing section. Item currency now
              always derives from the global setting
              `cdi.settings.currency` (loaded into `currencyCode`
              above). The dropdown JSX is gone; the state variable is
              retained so save payloads / snapshots / tax-config
              keying continue to work unchanged.

              FX Rate (Purchase) and FX Rate on Sale are HIDDEN from
              the UI everywhere (Step 2 spec). The underlying
              `fxRatePurchase` / `fxRateSale` state, save-payload
              wiring, and validation logic are intentionally left
              intact so no backend / schema / pricing math is
              touched. */}
        </div>
      </section>

      {/* Dynamic fields — responsive 2-col grid on ≥640px, single column on
          mobile.  Textareas (Notes) always span the full width.

          Schema fields are partitioned into TWO sections:
            • "Item Details" — every field that isn't a wine-specific
              tasting/drinking field. Original behaviour.
            • "Wine Details" — Drinking Window (Start/Peak/End), Food Pairing,
              Aroma, Palate, Finish, Rating. Rendered as a separate card
              underneath, with the SAME 2-col grid layout, so the wine-specific
              data has its own visual home without disturbing the existing
              top-of-form layout.                                              */}
      {(() => {
        const WINE_DETAILS = new Set([
          "Drinking Window Start",
          "Drinking Window Peak",
          "Drinking Window End",
          "Food Pairing",
          "Aroma",
          "Palate",
          "Finish",
          "Rating",
        ]);
        // Legacy single-value pricing fields — now fully superseded by the
        // top-level unified Buy/Sell × Ex/Inc GST grid in the Pricing card
        // above. Hide them everywhere in the dynamic form (incl. mobile)
        // so users see exactly one pricing surface. Their stored values
        // (if any) are still preserved in item_data; the unified grid is
        // the single source of truth going forward.
        const LEGACY_PRICING = new Set([
          "Purchase Price", "Purchase price", "purchase_price",
          "Estimated Value", "Estimated value", "estimated_value",
          "Buy Price", "Sell Price",
        ]);
        // Per-collection-type field suppression — hides schema-declared
        // fields that have been superseded by canonical UI elsewhere
        // on the form. Phase B regression-correction targets:
        //   • Wine — "Category" is a legacy free-text holdover that
        //     duplicates the collection-type concept; not part of the
        //     Phase A Wine preset.
        //   • Whisky / Spirits — the unified Quantity logic (Single
        //     Bottle / Case toggle) lives in its own dedicated card
        //     above, so the schema's "Quantity Owned" / "Quantity"
        //     fields would render a duplicate input. Hide both keys
        //     on those types only — the canonical Quantity card
        //     still drives item_data.quantity / .case_mode.
        // The stored value is preserved on save (we only filter the
        // RENDER, not the values map), so legacy items keep their data.
        const HIDDEN_FIELDS = new Set();
        if (isWineCollection) HIDDEN_FIELDS.add("Category");
        if (isWhiskyCollection || isSpiritsCollection) {
          HIDDEN_FIELDS.add("Quantity Owned");
          HIDDEN_FIELDS.add("Quantity");
        }
        // Spirits — strict identity whitelist. Per spec the Spirits form
        // renders EXACTLY the identity-block fields:
        //   Brand / Producer + Spirit Type + ABV + Bottle Size + Notes
        // (with `ABV %` accepted as a legacy alias for ABV). Every other
        // schema field on a Spirits category (Country, Region, Age,
        // Cask Type, Rating, …) is suppressed to keep the form clean
        // and industry-standard. Quantity is handled by the dedicated
        // Case Lot / Bottle Count card above — not a schema field.
        // Applies only when the active collection is Spirits; Wine /
        // Whisky / other categories keep their full schema. Stored
        // values are preserved (render-only filter).
        // Whitelist covers BOTH the naming-block fields (Brand, Item
        // Name, Age, Bottle Size, ABV — 2026-02 industry-standard) and
        // the identity metadata fields the user wants editable
        // (Spirit Type, Region, Country, Distillery / Producer,
        // Cask Type / Finish, Style, Finish Notes). Legacy compat
        // keys (Producer / Distillery, Type, Cask Type, ABV %) are
        // kept so existing items keep displaying their historical
        // values.
        // Spirits render-only field filter — Phase 3 (2026-02).
        //
        // Prior revision used an aggressive whitelist that suppressed
        // every schema field NOT in a short allow-list. Per the "restore
        // full Additional Item Details for Spirits" directive, that
        // whitelist is inverted into a targeted BLACKLIST — the Spirits
        // form now renders every schema field it has, except three
        // explicit exclusions:
        //   • `Age`         — legacy identity field, replaced by
        //                     `Age / Classification`. Stored values on
        //                     existing items are preserved in
        //                     item_data (render-only filter), they
        //                     just no longer surface.
        //   • `Drink From`  — Wine-only field; must not appear on
        //                     Spirits per Phase 3 spec.
        //   • `Drink To`    — Wine-only field; must not appear on
        //                     Spirits per Phase 3 spec.
        //
        // All other metadata and analytic fields (Cask Type / Finish,
        // Region, Country, Distillery / Producer, Style, Finish Notes,
        // Tasting Notes, Personal Notes, Image, Storage Location,
        // Purchase Date, Purchase Source, Sell Date, Sell Price,
        // Sell Channel, Sell Notes, Batch / Release ID, Bottling Year,
        // Brand Story, Region Notes, …) flow through untouched, so any
        // custom rows the user has added via Manage Fields render too.
        const SPIRITS_HIDDEN = isSpiritsCollection
          ? new Set(["Drink From", "Drink To"])
          : null;
        // The dedicated "Wine Details" section (Aroma / Palate / Finish /
        // Rating / Drinking Window / Food Pairing) is a Wine-specific
        // sub-grouping. It used to render for ANY collection that had
        // overlapping field names — that caused the bleed-through
        // regression where Whisky / Spirits items also displayed a
        // "Wine Details" card at the bottom. Restricted to Wine; on
        // other naming-enabled collections those fields (if present in
        // their schema) just fall through to Item Details naturally.
        const wineDetailsFields = isWineCollection
          ? fields.filter((f) => WINE_DETAILS.has(f.field_name) && !LEGACY_PRICING.has(f.field_name) && !HIDDEN_FIELDS.has(f.field_name))
          : [];
        const regularFields = fields.filter((f) => {
          if (LEGACY_PRICING.has(f.field_name)) return false;
          if (HIDDEN_FIELDS.has(f.field_name)) return false;
          if (isWineCollection && WINE_DETAILS.has(f.field_name)) return false; // belongs to wineDetailsFields
          if (SPIRITS_HIDDEN && SPIRITS_HIDDEN.has(f.field_name)) return false; // Spirits Phase 3 blacklist
          return true;
        });

        // Auto-Name Builder Fields — these schema fields drive the
        // composer for the active collection type AND now render inside
        // the AUTO-NAME card (NOT inside Item Details). Order matches
        // the user's spec (Wine: Producer, Appellation, Vintage; Whisky:
        // Distillery, Age, Cask Type; Spirits: Brand / Producer, Spirit
        // Type). Categories that lack a builder field simply skip that
        // Identity fields (Unified Category Field UI — patch A). Uses
        // the locked position-based lists from
        // `/lib/presetSchemas.js` so this stays in lock-step with the
        // backend `TYPE_MASTER_TEMPLATES`. For non-preset collection
        // types (general / custom / collectables) `builderOrder` is
        // empty — every field falls into "Additional Item Details".
        const builderOrder = getIdentityOrder(collectionTypeId);
        // 2026-02 — Unified Collector AUTO-NAME contains ONLY the fields
        // that participate in the composed name. All other fields render
        // in the dedicated "Identity Fields" section below. Field names
        // map to the existing preset schema so `presetSchemas.js` and
        // backend templates remain untouched.
        const UNIFIED_BUILDER_ORDER = {
          // Wine (2026-02 patch — Wine-only): Producer → Item Name (auto)
          //   → Region → Appellation → Grape(s) → Vintage → ABV →
          //   Bottle Size. Type / Country / Sub-Region / Classification
          //   / Colour / Sweetness remain metadata-only. Appellation
          //   moved into the identity block (Phase 6, 2026-02) so it
          //   sits alongside Region as an optional identity field
          //   rather than living in Additional Item Details.
          wine: ["Producer", "Item Name", "Region", "Appellation", "Grape(s)", "ABV", "Vintage", "Bottle Size", "Country", "Sub-Region", "Type", "Classification", "Cuvée"],
          // Whisky (Phase 5, 2026-02): Distillery → Item Name (auto) →
          //   Whisky Type → Age / Classification → ABV → Bottle Size.
          //   Legacy Age / Cask Type / Region rows stay on the schema
          //   but slide out of the naming engine (they render as
          //   metadata rows).
          whisky: ["Distillery", "Item Name", "Whisky Type", "Age / Classification", "ABV", "Bottle Size"],
          // Spirits (Phase 2, 2026-02): Brand → Item Name (auto) →
          //   Spirit Type → Age Classification → ABV → Bottle Size.
          //   The template's identity Age column was renamed from
          //   "Age" to "Age Classification" in the 2026-02-14 v2
          //   dropdown rebuild. Legacy Cask Type / Finish / Region
          //   rows stay on the schema but slide out of the naming
          //   engine (they render as metadata rows).
          spirits: ["Brand", "Item Name", "Spirit Type", "Age Classification", "ABV", "Bottle Size"],
        };
        const effectiveBuilderOrder = UNIFIED_BUILDER_ORDER[collectionTypeId] || builderOrder;
        const builderSet = new Set(effectiveBuilderOrder);
        // Render identity rows in the preset's canonical order. Any
        // identity field NOT present on the current category is
        // usually filtered out via `filter(Boolean)`. Whisky's
        // `Age / Classification` is the one exception: it does not
        // live on the backend Whisky template yet, so it's
        // SYNTHESIZED as an inline field so the Phase 5 layout can
        // still render it in the identity grid without a schema
        // change.
        const builderFields = effectiveBuilderOrder
          .map((name) => {
            const found = regularFields.find((f) => f.field_name === name);
            if (found) return found;
            if (isWhiskyCollection && name === "Age / Classification") {
              return {
                id: "synthetic-whisky-age-classification",
                field_name: "Age / Classification",
                field_type: "text",
                required: true,
                master: true,
              };
            }
            return null;
          })
          .filter(Boolean);
        // Item Details = everything left after the identity slice. Then
        // partition into two buckets: `supportingFields` (in-preset
        // supporting rows + user-added custom rows) and `legacyFields`
        // (master rows from an older preset revision that are no longer
        // in the current schema). The synthetic Item Name field gets
        // prepended later in the Additional Item Details grid.
        // 2026-02-26 — "Include Grape in Name?" is a Wine-only toggle
        // that renders next to the AUTO-NAME preview chip (not inside
        // Additional Item Details and not as a Legacy Field). Drop it
        // from `nonIdentityFields` so it never leaks into either
        // downstream bucket. Mirrors the `builderSet` exclusion above.
        const nonIdentityFields = regularFields.filter(
          (f) => !builderSet.has(f.field_name) && f.field_name !== "Include Grape in Name?"
        );
        const supportingFieldsAll = nonIdentityFields.filter(
          (f) => classifyPresetField(f, collectionTypeId) !== "legacy"
        );
        // Unified Collector — carve out an "Identity Fields" group per
        // preset track. These are metadata fields that used to live in
        // the auto-name block but no longer participate in the composed
        // name (Unified format only uses Brand + Expression + Age/Vintage
        // + Type + Bottle Size + ABV). Rendered as their own section
        // between AUTO-NAME and Additional Item Details on all three
        // preset tracks.
        const UNIFIED_IDENTITY_METADATA = {
          wine: new Set(["Colour", "Sweetness"]),
          whisky: new Set([
            "Cask Type", "Distillery / Producer",
            "Style", "Finish Notes",
            "Country", "Region", "Cask Finish",
            "Distillation Year", "Bottling Year",
            "Batch / Barrel Number",
          ]),
          spirits: new Set([
            "Spirit Type",
            "Distillery / Producer", "Cask Type / Finish",
            "Style", "Finish Notes",
            "Country", "Region",
            // Legacy compat keys mapped into Identity too
            "Producer / Distillery", "Type", "Cask Type",
          ]),
        };
        const identityMetadataSet =
          UNIFIED_IDENTITY_METADATA[collectionTypeId] || new Set();
        // 2026-02-26 — Identity Metadata pulls from `nonIdentityFields`
        // rather than `supportingFieldsAll` so master-template fields
        // that were removed from `presetSchemas.js` supporting arrays
        // (e.g. Whisky's Country / Region / Cask Finish / Distillation
        // Year / Bottling Year / Batch / Barrel Number) still surface
        // in the Identity Metadata block. Otherwise the intermediate
        // `!== "legacy"` filter drops them before they can be picked
        // up here, matching the user's move-to-metadata intent.
        const identityMetadataFields =
          identityMetadataSet.size > 0
            ? nonIdentityFields.filter((f) => identityMetadataSet.has(f.field_name))
            : [];
        const supportingFields =
          identityMetadataSet.size > 0
            ? supportingFieldsAll.filter((f) => !identityMetadataSet.has(f.field_name))
            : supportingFieldsAll;
        // Backwards-compat alias — the render block still references
        // `spiritsIdentityFields` for the section it originally shipped.
        const spiritsIdentityFields = identityMetadataFields;
        const legacyFields = nonIdentityFields.filter(
          (f) => classifyPresetField(f, collectionTypeId) === "legacy"
            && !identityMetadataSet.has(f.field_name)
        );
        // Legacy fields are shown ONLY when they actually hold data.
        // Empty legacy rows are hidden entirely — the "Reset to Preset"
        // button (patch B) will offer to delete them from the schema.
        const legacyFieldsWithData = legacyFields.filter((f) => {
          const v = values[f.field_name];
          if (v === undefined || v === null) return false;
          if (typeof v === "string") return v.trim().length > 0;
          if (Array.isArray(v)) return v.length > 0;
          return true;
        });
        // Kept for the existing footer counter — total field count
        // shown next to the Additional Item Details heading. Excludes
        // legacy rows (they render in their own section).
        const detailsFields = supportingFields;
        const renderField = (f) => (
          <DynamicField
            key={f.id}
            f={f}
            value={values[f.field_name]}
            onChange={(v) => {
              setFieldValue(f.field_name, v);
              // Clear an existing required-field error as soon as the user
              // starts entering a valid value (no need to wait for blur).
              if (fieldErrors[f.field_name] && f.required) {
                const empty = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
                if (!empty) {
                  setFieldErrors((prev) => {
                    const copy = { ...prev }; delete copy[f.field_name]; return copy;
                  });
                }
              }
            }}
            onBlur={() => validateField(f.field_name)}
            error={fieldErrors[f.field_name]}
            presets={presets[f.field_name] || []}
            itemNames={nameSuggestions}
            fieldValues={fieldValues[f.field_name] || []}
            // Optimistic add — push the just-added value into the in-memory
            // fieldValues cache for this field so the dropdown surfaces it
            // immediately, before any save round-trip. Undo removes it
            // again. Both are pure session-local cache mutations; the
            // persisted item / schema is untouched.
            onAddValueOptimistic={(value) => {
              const trimmed = String(value || "").trim();
              if (!trimmed) return;
              setFieldValues((prev) => {
                const existing = prev[f.field_name] || [];
                if (existing.some((v) => v.toLowerCase() === trimmed.toLowerCase())) return prev;
                const next = [...existing, trimmed].sort((a, b) =>
                  a.toLowerCase().localeCompare(b.toLowerCase())
                );
                return { ...prev, [f.field_name]: next };
              });
              // Mark this value as session-only so ComboBox renders the
              // italic ★ marker until the next save (post-save rehydrate
              // clears the entire optimistic set).
              setOptimisticValues((prev) => {
                const cur = prev[f.field_name] || new Set();
                if ([...cur].some((v) => v.toLowerCase() === trimmed.toLowerCase())) return prev;
                const next = new Set(cur);
                next.add(trimmed);
                return { ...prev, [f.field_name]: next };
              });
            }}
            onUndoAddValue={(value) => {
              const trimmed = String(value || "").trim();
              if (!trimmed) return;
              // Drop the value from the session cache.
              setFieldValues((prev) => {
                const existing = prev[f.field_name] || [];
                const next = existing.filter((v) => v.toLowerCase() !== trimmed.toLowerCase());
                if (next.length === existing.length) return prev;
                return { ...prev, [f.field_name]: next };
              });
              // Remove the ★ marker as well — Undo is the explicit
              // "didn't mean to add that" path, so the optimistic state
              // for this value is no longer relevant.
              setOptimisticValues((prev) => {
                const cur = prev[f.field_name];
                if (!cur) return prev;
                const next = new Set([...cur].filter((v) => v.toLowerCase() !== trimmed.toLowerCase()));
                if (next.size === cur.size) return prev;
                return { ...prev, [f.field_name]: next };
              });
              // Clear the form value if it currently equals the undone value
              // so the user sees the dropdown collapse the entry AND lose
              // its selection in one click. Multi-select fields filter out
              // the value from the array instead.
              setValues((prev) => {
                const cur = prev[f.field_name];
                if (Array.isArray(cur)) {
                  const filtered = cur.filter((v) => String(v).toLowerCase() !== trimmed.toLowerCase());
                  if (filtered.length === cur.length) return prev;
                  return { ...prev, [f.field_name]: filtered };
                }
                if (String(cur || "").toLowerCase() === trimmed.toLowerCase()) {
                  return { ...prev, [f.field_name]: "" };
                }
                return prev;
              });
            }}
            optimisticSet={optimisticValues[f.field_name]}
            collectionTypeId={collectionTypeId}
          />
        );
        return (
          <>
            {/* Auto-Name Preview — unified card for Wine / Whisky / Spirits.
                Phase B correction: header label sits ABOVE the preview chip
                so the composed name reads as a standalone preview, not an
                inline tag. The MANUAL revert pill anchors to the header
                row when an override is active. Composer inputs (per
                Phase B):
                  Wine    → Producer + Appellation + Vintage
                  Whisky  → Distillery + Age + Cask Type
                  Spirits → Brand / Producer + Spirit Type
                The actual composition runs in the upstream useEffects;
                this is a read-only preview. Hidden on non-naming
                collections. */}
            {(isWineCollection || isWhiskyCollection || isSpiritsCollection) && (() => {
              const composed = composeAuto(values);
              // Note: `values["Name Override"]` is intentionally NOT
              // surfaced in the UI — the MANUAL pill was removed in
              // Phase 8 (2026-02). The flag still functions internally
              // via `setFieldValue` + the per-preset auto-build
              // useEffects; the render layer just no longer exposes it.
              // AUTO-NAME preview is ALWAYS the composed identity string —
              // it never mirrors the manually typed Item Name so users can
              // see what the auto-composer *would* produce even while an
              // override is pinned. The MANUAL pill still surfaces the
              // override state; the Item Name write paths are untouched.
              const display = composed;
              const labelMap = {
                wine:    { recipe: "Producer · [Appellation OR Region] · Grape(s) · Vintage · ABV · Bottle Size",       hint: "Start typing Producer to compose…",         testid: "ie-wine-name-preview",    manualTestid: "ie-wine-name-preview-manual",    bannerTestid: "ie-auto-name-banner-wine",    plain: "Wine items are named as Producer + Appellation (falls back to Region when Appellation is blank) + Grape(s) + Vintage + ABV + Bottle Size.",    parts: ["Producer", "Appellation (or Region)", "Grape(s)", "Vintage", "ABV", "Bottle Size"] },
                whisky:  { recipe: "Distillery · Whisky Type · Age / Classification · ABV · Bottle Size",         hint: "Start typing Distillery to compose…",       testid: "ie-whisky-name-preview",  manualTestid: "ie-whisky-name-preview-manual",  bannerTestid: "ie-auto-name-banner-whisky",  plain: "Whisky items are named as Distillery + Whisky Type + Age / Classification + ABV + Bottle Size.",                                                  parts: ["Distillery", "Whisky Type", "Age / Classification", "ABV", "Bottle Size"] },
                spirits: { recipe: "Brand · Spirit Type · Age Classification · ABV · Bottle Size",        hint: "Start typing Brand to compose…", testid: "ie-spirits-name-preview", manualTestid: "ie-spirits-name-preview-manual", bannerTestid: "ie-auto-name-banner-spirits", plain: "Spirits items are named as Brand + Spirit Type + Age Classification + ABV + Bottle Size.",                                                parts: ["Brand", "Spirit Type", "Age Classification", "ABV", "Bottle Size"] },
              };
              const kind = isWineCollection ? "wine" : isWhiskyCollection ? "whisky" : "spirits";
              const meta = labelMap[kind];
              const tooltip = `Item Name is auto-built as ${meta.recipe}. Type into Item Name to customise it; clear it to resume the auto-build.`;
              // ------------------------------------------------------------------
              // Phase 5 (2026-02) — extract every part of the AUTO-NAME
              // card into named JSX fragments so the section body can
              // reorder them per track without duplicating markup.
              //
              // Wine + Spirits order:
              //   identityGrid → headerRow → previewChip → itemNameOverride
              // Whisky order (unchanged):
              //   headerRow → previewChip → identitySubtitle → identityGrid
              // ------------------------------------------------------------------
              const headerRow = (
                <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
                  <div className="flex items-baseline gap-2">
                    <h2 className="text-xs uppercase tracking-wider vp-muted">Auto-Name</h2>
                    <span className="text-[10px] vp-muted">{meta.recipe}</span>
                    {/* Plain-English popover — discoverability without
                        crowding the chip. Radix Popover handles outside-
                        click auto-close + portaling, so the popover
                        doesn't shift the surrounding layout. */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid={`ie-auto-name-info-${kind}`}
                          aria-label="What is Auto-Name?"
                          title="What is Auto-Name?"
                          className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] hover:bg-[hsl(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))] transition-colors"
                        >
                          <Info size={12} strokeWidth={2} />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="bottom"
                        align="start"
                        sideOffset={6}
                        className="w-64 p-3 text-xs leading-relaxed"
                        data-testid={`ie-auto-name-info-content-${kind}`}
                      >
                        <p className="font-medium mb-1.5">How Auto-Name works</p>
                        <p className="vp-muted mb-2">{meta.plain}</p>
                        <ul className="vp-muted space-y-0.5 list-disc pl-4">
                          {meta.parts.map((p) => <li key={p}>{p}</li>)}
                        </ul>
                        <p className="vp-muted mt-2 text-[10px]">Type into <b>Item Name</b> to override; clear it to resume the auto-build.</p>
                      </PopoverContent>
                    </Popover>
                  </div>
                  {/* MANUAL pill removed — UI no longer surfaces the
                      internal `Name Override` flag anywhere on-screen
                      (Phase 8, 2026-02). The flag still functions
                      internally: typing into Item Name pins it, and
                      clearing Item Name lifts it — see
                      `setFieldValue` above and each preset's
                      auto-build useEffect. */}
                </div>
              );
              const previewChip = (
                <div
                  data-testid={meta.testid}
                  className={`inline-flex items-center max-w-full rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] px-3 py-2 text-sm font-medium ${display ? "" : "vp-muted italic font-normal"}`}
                >
                  <span className="truncate">{display || meta.hint}</span>
                </div>
              );
              // Identity Preview const removed in Phase 5 (was only
              // used by the pre-Phase-5 Whisky layout, which now
              // renders identity/AUTO-NAME/override in the same
              // strict order as Wine + Spirits). Kept as a code
              // comment so the omission is intentional and searchable.

              // Identity Fields — drives naming and identity. All
              // rows are REQUIRED by convention (position-based
              // per Unified Category Field UI, patch A) and
              // LOCKED as part of the CDI preset — they cannot be
              // removed via Manage Fields. Per-type order is
              // driven by /lib/presetSchemas.js and stays in
              // lock-step with the backend TYPE_MASTER_TEMPLATES.
              // Rendered via the shared `renderField` so dropdown
              // hydration / ComboBox / "+ Add" / ★ marker / undo
              // all still apply. Missing preset rows simply skip
              // (older categories that haven't re-fetched yet).
              //
              // Wine + Spirits: `Item Name` is EXCLUDED from this
              // grid — it renders as a dedicated synthetic override
              // input below the AUTO-NAME preview chip so the
              // block reads identity → AUTO-NAME → Item Name
              // (override). Phase 5 extends this to Whisky. The
              // underlying UNIFIED_BUILDER_ORDER / builderSet /
              // composer logic are untouched.
              const identityGrid = (() => {
                const identityGridFields = (isWineCollection || isSpiritsCollection || isWhiskyCollection)
                  ? builderFields.filter((bf) => bf.field_name !== "Item Name")
                  : builderFields;
                if (identityGridFields.length === 0) return null;
                return (
                  <div data-testid={`ie-identity-section-${kind}`}>
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wider">Identity Fields</h3>
                        <p className="text-[11px] vp-muted mt-0.5">Drives Naming &amp; Identity</p>
                      </div>
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border border-[hsl(var(--gold))] text-[hsl(var(--gold))] bg-[hsl(var(--gold)/0.08)]"
                        title="These fields are required and part of the CDI preset. They cannot be removed."
                        data-testid="ie-identity-badge"
                      >
                        <Lock size={10} /> Required &middot; CDI Preset
                      </span>
                    </div>
                    <div
                      className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4"
                      data-testid={`ie-auto-name-builder-${kind}`}
                    >
                      {identityGridFields.map((f) => {
                        // Wine identity optionality (Phase 6, 2026-02):
                        // every Wine identity field except Producer
                        // renders with the "Optional" badge and
                        // `required: false`. Producer stays REQUIRED —
                        // it's the mandatory Wine naming anchor.
                        // Spirits + Whisky identity fields remain
                        // required by the position-based rule and are
                        // untouched by this branch.
                        const WINE_OPTIONAL_IDENTITY = new Set([
                          "Region", "Appellation", "Grape(s)",
                          "Vintage", "ABV", "Bottle Size",
                        ]);
                        const isOptionalIdentity =
                          isWineCollection && WINE_OPTIONAL_IDENTITY.has(f.field_name);
                        if (isOptionalIdentity) {
                          return (
                            <div
                              key={f.id}
                              className="relative"
                              data-testid="ie-identity-optional-wrap"
                            >
                              <span
                                className="absolute -top-1 right-0 inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-[1px] rounded-full border border-[hsl(var(--border))] vp-muted bg-[hsl(var(--bg-elevated))]"
                                title="Optional Wine identity field. Sits in the Identity block for naming order, but you can leave it blank."
                                data-testid={`ie-identity-optional-badge-${f.field_name}`}
                              >
                                Optional
                              </span>
                              {renderField({ ...f, required: false })}
                            </div>
                          );
                        }
                        return renderField({ ...f, required: true });
                      })}
                    </div>
                  </div>
                );
              })();
              // Synthetic Item Name override (Wine + Spirits + Whisky
              // via Phase 5) — hooks into setFieldValue("Item Name", …)
              // which routes through the per-track override contract
              // defined earlier in this file. Renders unconditionally
              // on all three preset tracks even if the schema does not
              // declare an "Item Name" row.
              const itemNameOverride = (isWineCollection || isSpiritsCollection || isWhiskyCollection) ? (
                <div
                  data-testid={`ie-${kind}-item-name-override-wrap`}
                >
                  {renderField({
                    id: `synthetic-item-name-${kind}`,
                    field_name: "Item Name",
                    field_type: "text",
                    required: true,
                    labelText: "Item Name (edit this field to customise item name)",
                  })}
                </div>
              ) : null;
              // Wine + Spirits + Whisky (Phase 5, 2026-02): strict order
              //   identityGrid → headerRow → previewChip → itemNameOverride
              // No other tracks reach this branch — the outer gate
              // (isWineCollection || isWhiskyCollection || isSpiritsCollection)
              // ensures only preset tracks land here.
              return (
                <section className="vp-card p-6 mb-6" data-testid={meta.bannerTestid} title={tooltip}>
                  {identityGrid}
                  <div className="mt-6">{headerRow}</div>
                  <div className="-mt-1 flex flex-wrap items-center gap-3">
                    {previewChip}
                    {isWineCollection && (() => {
                      // Render the "Include Grape in Name?" toggle right
                      // next to the AUTO-NAME preview chip so the switch
                      // sits with the name it controls. Guarded against
                      // undefined — if the master template hasn't seeded
                      // the field yet (older Wine categories, migration
                      // not run), the fragment collapses silently.
                      const toggleField = regularFields.find(
                        (f) => f.field_name === "Include Grape in Name?"
                      );
                      return toggleField ? renderField(toggleField) : null;
                    })()}
                  </div>
                  <div className="mt-4">{itemNameOverride}</div>
                </section>
              );
            })()}


            <section className="vp-card p-6 mb-6" data-testid="ie-fields-section">
              {/* Unified Collector — dedicated "Identity Fields" block
                  for metadata that no longer participates in the auto-
                  name string (Region, Country, Grapes, Appellation,
                  Classification, Cask Type / Finish, Style, Distillery /
                  Producer, Finish Notes, etc). Rendered only when at
                  least one such field is present on the active
                  category. Sits above Additional Item Details so it's
                  visible immediately after AUTO-NAME. Applies to Wine,
                  Whisky, and Spirits. */}
              {(isWineCollection || isWhiskyCollection || isSpiritsCollection) &&
                spiritsIdentityFields.length > 0 && (
                <div className="mb-6" data-testid="ie-identity-metadata-section">
                  <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                    <div>
                      <h2 className="text-sm font-semibold">Identity Fields</h2>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider vp-muted">
                      {spiritsIdentityFields.length} field{spiritsIdentityFields.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                    {spiritsIdentityFields.map(renderField)}
                  </div>
                </div>
              )}
              <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                <div>
                  <h2 className="text-sm font-semibold">Additional Item Details</h2>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border border-[hsl(var(--border))] vp-muted"
                    title="These fields are optional. Fill any that apply; leave the rest blank."
                    data-testid="ie-additional-badge"
                  >
                    Optional
                  </span>
                  {categoryId && detailsFields.length > 0 && (
                    <span className="text-[10px] uppercase tracking-wider vp-muted">
                      {detailsFields.length} field{detailsFields.length === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              </div>
              {!categoryId ? (
                <p className="text-sm vp-muted">Pick a category above to load its fields.</p>
              ) : loadingFields ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="vp-skeleton h-10 rounded-md" />
                  <div className="vp-skeleton h-10 rounded-md" />
                  <div className="vp-skeleton h-10 rounded-md" />
                  <div className="vp-skeleton h-10 rounded-md" />
                </div>
              ) : detailsFields.length === 0 && !(isWineCollection || isWhiskyCollection || isSpiritsCollection) ? (
                <p className="text-sm vp-muted">This category has no fields defined.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                  {/* Legacy synthetic Item Name render — REMOVED in
                      Phase 5 (2026-02). Wine, Spirits, and Whisky all
                      now render Item Name as a synthetic override
                      inside the AUTO-NAME identity card (see the
                      `itemNameOverride` fragment in the preset section
                      above). Rendering another copy here would
                      double-bind to values["Item Name"] and confuse
                      the override write path. */}
                  {detailsFields.map(renderField)}
                </div>
              )}
            </section>

            {/* Legacy Fields (Read-Only) — hidden globally per user
                directive 2026-02. Backend quantity engine and all
                shared field state remain untouched; this container is
                simply not rendered anywhere in the UI for any category
                (Whisky / Wine / Spirits / etc.). To re-enable, restore
                the original condition:
                `categoryId && !loadingFields && legacyFieldsWithData.length > 0`. */}
            {false && (
              <section className="vp-card p-6 mb-6" data-testid="ie-legacy-fields-section">
                <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <h2 className="text-sm font-semibold">Legacy Fields</h2>
                    <p className="text-[11px] vp-muted mt-0.5">
                      Held for historical reference. Not part of the current CDI preset.
                    </p>
                  </div>
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border border-[hsl(var(--border))] vp-muted"
                    title="These fields are from an older preset revision and are shown read-only."
                    data-testid="ie-legacy-badge"
                  >
                    Read-Only
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                  {legacyFieldsWithData.map((f) => (
                    <div key={f.id} data-testid={`ie-legacy-field-${f.id}`}>
                      <label className="block text-xs font-medium mb-1.5 vp-muted">
                        {f.field_name}
                      </label>
                      <div className="w-full rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] px-3 py-2 text-sm vp-muted truncate">
                        {(() => {
                          const v = values[f.field_name];
                          if (v === undefined || v === null || v === "") return "—";
                          if (Array.isArray(v)) return v.join(", ");
                          return String(v);
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Quantity — Model A (industry-standard):
                  Total Bottles   (primary user input)
                  Case Size       (optional dropdown: 6 / 12 / 24 / custom)
                  Full Cases      = floor(Total Bottles / Case Size)   [derived, read-only]
                  Loose Bottles   = Total Bottles % Case Size           [derived, read-only]

                When Case Size is unset the derived rows collapse and only
                Total Bottles is shown. All values persist under new
                item_data keys (`Total Bottles`, `Case Size`) — no schema
                changes. Legacy keys (Bottle Count / Case Mode / Units in
                Case / Case Type) are NOT read here; they remain dormant
                on older items per the migration rule. Renders identically
                across Wine, Whisky, and Spirits. */}
            {categoryId && !loadingFields && (() => {
              const totalRaw = values["Total Bottles"];
              const caseSizeRaw = values["Case Size"];
              const totalBottles = Number.isFinite(Number(totalRaw)) && Number(totalRaw) > 0 ? Number(totalRaw) : 0;
              const caseSize = Number.isFinite(Number(caseSizeRaw)) && Number(caseSizeRaw) > 0 ? Number(caseSizeRaw) : 0;
              const fullCases = caseSize > 0 ? Math.floor(totalBottles / caseSize) : 0;
              // 2026-02-26 — when Case Size is null / 0, ALL bottles are
              // treated as loose (per Quantity Migration spec). Previously
              // this returned 0 which lost the loose count entirely.
              const looseBottles = caseSize > 0 ? totalBottles % caseSize : totalBottles;

              const buy = Number(buyEx || buyInc || purchasePrice || 0);
              // resolveValuation() spec — per-bottle Sell / Valuation is
              // driven by the source selector, not the raw market
              // input. Market → market_valuation_ex_gst;
              // Manual → manual_valuation_ex_gst; Computed → skipped
              // here (weighted-average needs sale history, computed
              // on the read side). Fallback → Buy Price.
              const marketNum = Number(marketValueEx || marketValueInc || estimatedValue || 0);
              const manualNum = Number(manualValueEx || 0);
              let sellEntered = 0;
              let sourceFoot = "";
              if (valuationSource === "Market" && marketNum > 0) {
                sellEntered = marketNum; sourceFoot = "Market";
              } else if (valuationSource === "Manual" && manualNum > 0) {
                sellEntered = manualNum; sourceFoot = "Manual";
              } else if (valuationSource === "Computed") {
                // Preview only — actual Computed uses sale history at
                // read time. Show 0 until a sale is recorded.
                sellEntered = 0; sourceFoot = "Computed (from sales)";
              }
              const sell = sellEntered > 0 ? sellEntered : buy;
              const sellIsFallback = sellEntered <= 0 && buy > 0;
              const unitPriceBuy = buy;
              const unitPriceSell = sell;
              const totalCost = buy * totalBottles;
              const totalValue = sell * totalBottles;
              const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

              return (
                <section className="vp-card p-6 mb-6" data-testid="ie-case-section">
                  <div className="flex items-baseline justify-between mb-4">
                    <h2 className="text-sm font-semibold">Quantity</h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                    {/* Total Bottles — primary input */}
                    <div>
                      <label htmlFor="ie-total-bottles" className="block text-xs font-medium mb-1.5 vp-muted">
                        Total Bottles
                      </label>
                      <input
                        id="ie-total-bottles"
                        data-testid="ie-total-bottles"
                        type="number"
                        inputMode="numeric"
                        min="1"
                        step="1"
                        value={values["Total Bottles"] ?? ""}
                        onChange={(e) => setFieldValue("Total Bottles", e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        aria-invalid={Boolean(fieldErrors["Total Bottles"]) || undefined}
                        aria-describedby={fieldErrors["Total Bottles"] ? "ie-total-bottles-error" : undefined}
                        className={`vp-input ${fieldErrors["Total Bottles"] ? "border-[hsl(var(--loss))] focus:ring-[hsl(var(--loss))]" : ""}`}
                        placeholder="e.g. 12"
                      />
                      {fieldErrors["Total Bottles"] && (
                        <p
                          id="ie-total-bottles-error"
                          data-testid="ie-total-bottles-error"
                          className="mt-1 text-[11px] text-[hsl(var(--loss))]"
                        >
                          {fieldErrors["Total Bottles"]}
                        </p>
                      )}
                    </div>

                    {/* Case Size — optional dropdown */}
                    <div>
                      <label htmlFor="ie-case-size" className="block text-xs font-medium mb-1.5 vp-muted">
                        Case Size <span className="vp-subtle text-[10px]">(optional)</span>
                      </label>
                      <select
                        id="ie-case-size"
                        data-testid="ie-case-size"
                        value={values["Case Size"] ?? ""}
                        onChange={(e) => setFieldValue("Case Size", e.target.value)}
                        className="vp-select"
                      >
                        <option value="">— None —</option>
                        <option value="1">1</option>
                        <option value="3">3</option>
                        <option value="6">6</option>
                        <option value="12">12</option>
                      </select>
                    </div>
                  </div>

                  {/* Derived read-only rows — only shown when Case Size is set. */}
                  {caseSize > 0 && (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ie-quantity-derived">
                      <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider vp-muted">Full Cases</div>
                        <div className="text-sm font-medium" data-testid="ie-derived-full-cases">{fullCases}</div>
                        <div className="text-[10px] vp-subtle mt-0.5">floor({totalBottles} ÷ {caseSize})</div>
                      </div>
                      <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider vp-muted">Loose Bottles</div>
                        <div className="text-sm font-medium" data-testid="ie-derived-loose-bottles">{looseBottles}</div>
                        <div className="text-[10px] vp-subtle mt-0.5">{totalBottles} mod {caseSize}</div>
                      </div>
                      <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider vp-muted">Total Bottles</div>
                        <div className="text-sm font-medium" data-testid="ie-derived-total-bottles">{totalBottles}</div>
                        <div className="text-[10px] vp-subtle mt-0.5">{fullCases} × {caseSize} + {looseBottles}</div>
                      </div>
                    </div>
                  )}

                  {/* Valuation readouts — industry-standard 4-tile grid:
                        • Buy Price (Ex GST) — per bottle
                        • Market Value / Valuation — per bottle
                        • Total Cost   = Buy × Total Bottles
                        • Total Value  = Sell × Total Bottles
                                         (defaults to Total Cost when
                                          Sell is empty)
                      Total Bottles never multiplies the unit prices; it
                      only scales the totals. Only renders when we have
                      both a bottle count and at least one price signal. */}
                  {totalBottles > 0 && (buy > 0 || sellEntered > 0) && (
                    <dl className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-y-3 gap-x-4" data-testid="ie-case-readout">
                      <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] px-3 py-2 whitespace-nowrap">
                        <div className="text-[10px] uppercase tracking-wider vp-muted whitespace-nowrap">Buy Price</div>
                        <div className="text-sm font-medium whitespace-nowrap" data-testid="ie-readout-unit-price-buy">{fmt(unitPriceBuy)}</div>
                        <div className="text-[10px] vp-subtle mt-0.5 whitespace-nowrap">Per bottle (Ex GST)</div>
                      </div>
                      <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] px-3 py-2 whitespace-nowrap">
                        <div className="text-[10px] uppercase tracking-wider vp-muted whitespace-nowrap">Sell / Valuation</div>
                        <div className="text-sm font-medium whitespace-nowrap" data-testid="ie-readout-unit-price-sell">{fmt(unitPriceSell)}</div>
                        <div className="text-[10px] vp-subtle mt-0.5 whitespace-nowrap">
                          {sellIsFallback ? "Fallback: Buy Price (no valuation set)" : (sourceFoot || "Per bottle")}
                        </div>
                      </div>
                      <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] px-3 py-2 whitespace-nowrap">
                        <div className="text-[10px] uppercase tracking-wider vp-muted whitespace-nowrap">Total Cost</div>
                        <div className="text-sm font-medium whitespace-nowrap" data-testid="ie-readout-total-cost">{fmt(totalCost)}</div>
                        <div className="text-[10px] vp-subtle mt-0.5 whitespace-nowrap">{fmt(buy)} × {totalBottles}</div>
                      </div>
                      <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] px-3 py-2 whitespace-nowrap">
                        <div className="text-[10px] uppercase tracking-wider vp-muted whitespace-nowrap">Total Value</div>
                        <div className="text-sm font-medium whitespace-nowrap" data-testid="ie-readout-total-value">{fmt(totalValue)}</div>
                        <div className="text-[10px] vp-subtle mt-0.5 whitespace-nowrap">{fmt(sell)} × {totalBottles}</div>
                      </div>
                    </dl>
                  )}
                </section>
              );
            })()}

            {categoryId && !loadingFields && wineDetailsFields.length > 0 && (
              <section className="vp-card p-6 mb-6" data-testid="ie-wine-details-section">
                <div className="flex items-baseline justify-between mb-4">
                  <h2 className="text-sm font-semibold">Wine Details</h2>
                  <span className="text-[10px] uppercase tracking-wider vp-muted">
                    {wineDetailsFields.length} field{wineDetailsFields.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                  {wineDetailsFields.map(renderField)}
                </div>
              </section>
            )}
          </>
        );
      })()}

      <div className="flex flex-wrap justify-end gap-2 items-center">
        <button
          type="button"
          onClick={() => save()}
          disabled={saving || !collectionId || !categoryId || loadingItem}
          data-testid="ie-save"
          className="vp-btn vp-btn-primary disabled:opacity-50"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          {saving ? "Saving…" : isEdit ? "Update Item" : "Save Item"}
        </button>
      </div>

      <BottomNav current="items" />
    </div>
  );
}

/* ============================================================
   Renders the correct input widget for a category-field schema.
   ============================================================ */
function DynamicField({ f, value, onChange, onBlur, error, presets = [], itemNames = [], fieldValues = [], onAddValueOptimistic, onUndoAddValue, optimisticSet, collectionTypeId }) {
  const id = `ie-field-${f.id}`;
  const datalistId = `ie-presets-${f.id}`;
  // Fires the first time the user creates a new value for this field
  // name in the current browser tab. Pushes the value into the live
  // fieldValues cache (so the dropdown surfaces it immediately) and
  // shows a Sonner toast with an Undo action. Clicking Undo removes the
  // value from the cache + clears the form value, and resets the
  // per-field gate so the next add to this field shows the toast again.
  // Subsequent adds (without Undo) stay silent until a hard refresh.
  const onAddNotify = (newValue) => {
    const trimmed = String(newValue || "").trim();
    if (!trimmed) return;
    // Optimistic cache update — always run, even on subsequent silent adds,
    // so the dropdown picks up brand-new values right away.
    if (onAddValueOptimistic) onAddValueOptimistic(trimmed);
    if (_addedFieldToastShown.has(f.field_name)) return;
    _addedFieldToastShown.add(f.field_name);
    toast.success(`Added "${trimmed}" to ${f.field_name} — available immediately.`, {
      action: onUndoAddValue
        ? {
            label: "Undo",
            onClick: () => {
              onUndoAddValue(trimmed);
              // Reset the gate so a future add to this same field shows
              // the toast again — Undo invalidates the "first-add" mark.
              _addedFieldToastShown.delete(f.field_name);
            },
          }
        : undefined,
    });
  };
  const labelText = f.labelText
    ? (f.required ? <>{f.labelText} <span className="text-[hsl(var(--loss))]">*</span></> : f.labelText)
    : (f.required
        ? <>{f.field_name} <span className="text-[hsl(var(--loss))]">*</span></>
        : f.field_name);
  const label = <label htmlFor={id} className="block text-xs font-medium mb-1.5 vp-muted">{labelText}</label>;
  // Inline required-field error block — uses the same `--loss` token as the
  // required-asterisk marker so the styling is visually consistent across
  // every error surface in the form. Rendered after the input inside the
  // existing field wrapper so neither layout nor field structure changes.
  const errorNode = error ? (
    <p data-testid={`ie-field-error-${f.field_name}`} className="text-xs text-[hsl(var(--loss))] mt-1">
      {error}
    </p>
  ) : null;
  const testid = `ie-field-${f.field_name}`;
  // Track-aware placeholder hints. Wine keeps its historic Bourgogne
  // Rouge / France / Burgundy examples; Whisky and Spirits get on-brand
  // examples aligned with their master templates (Scotland/Speyside for
  // Whisky, USA/Kentucky for Spirits). Unknown collection types fall
  // back to the Wine map so the pre-existing placeholders keep firing.
  const PLACEHOLDERS_BY_TYPE = {
    wine: {
      "Item Name": "e.g. Bourgogne Rouge 2021",
      "Producer": "e.g. Domaine Anne Gros",
      "Country": "e.g. France",
      "Region": "e.g. Burgundy",
      "Sub-Region": "e.g. Cote de Nuits",
      "Appellation": "e.g. Bourgogne AOC",
      "Category": "e.g. Red, White, Sparkling",
      "Grape(s)": "e.g. Pinot Noir",
      "Bottle Size": "e.g. 750ml",
    },
    whisky: {
      "Item Name": "e.g. Glenfiddich 12 Year",
      "Country": "e.g. Scotland",
      "Region": "e.g. Speyside",
      "Bottle Size": "e.g. 750ml",
    },
    spirits: {
      "Item Name": "e.g. Don Julio Blanco",
      "Country": "e.g. USA",
      "Region": "e.g. Kentucky",
      "Bottle Size": "e.g. 750ml",
    },
  };
  const PLACEHOLDERS = PLACEHOLDERS_BY_TYPE[collectionTypeId] || PLACEHOLDERS_BY_TYPE.wine;
  const NUM_PLACEHOLDERS = { Vintage: "2021", Rating: "0–100" };
  const placeholder = PLACEHOLDERS[f.field_name] || "";
  // Well-known fields that should render as a combobox: shows a clickable
  // chevron with the FULL list (common defaults + presets) on focus, allows
  // typing new values, and persists them through the normal item-save flow
  // (auto-picked up by /api/presets on the next load).
  const COMMON_OPTIONS = {
    "Bottle Size": ["187ml", "375ml", "500ml", "750ml", "1L", "1.5L", "3L", "6L"],
    "Grape(s)": [
      "Pinot Noir",
      "Chardonnay",
      "Cabernet Sauvignon",
      "Merlot",
      "Syrah",
      "Shiraz",
      "Sauvignon Blanc",
      "Riesling",
      "Pinot Gris",
      "Gewürztraminer",
      "Grenache",
      "Tempranillo",
      "Sangiovese",
      "Nebbiolo",
      "Malbec",
      "Zinfandel",
      "Cabernet Franc",
      "Petit Verdot",
      "Viognier",
      "Semillon",
      "Chenin Blanc",
    ],
    "Wine Type": ["Red", "White", "Rosé", "Sparkling", "Dessert", "Fortified"],
    "Type": ["Red", "White", "Rosé", "Sparkling", "Dessert", "Fortified"],
    // Spirits Age / Classification (Phase 2, 2026-02) — kept in
    // lock-step with backend `_SPIRITS_AGE_CLASS_OPTS`. ComboBox lets
    // users pick a preset or type a free-form value (e.g. "12yr").
    "Age / Classification": [
      "Blanco", "Joven", "Reposado", "Añejo", "Extra Añejo",
      "VS", "VSOP", "XO",
      "Reserva", "Solera", "NAS",
    ],
    // Open-vocab fields — no canonical defaults, the ComboBox just surfaces
    // values typed previously (via /api/items/field-values) plus "+ Add".
    "Producer": [],
    "Producer / Distillery": [],
    "Country": [],
    "Region": [],
    "Sub-Region": [],
    "Sub Region": [],
    "Subregion": [],
    "Appellation": [],
    "Distillery": [],
    "Cask Type": [],
    "Release Type": [],
  };
  const comboDefaults = COMMON_OPTIONS[f.field_name];
  // Layout: textareas + the canonical Item Name span the full 2-col grid row;
  // everything else takes a single cell on sm+, full width on mobile.
  const fullRow = f.field_type === "textarea" || f.field_name === "Item Name";
  const spanClass = fullRow ? "sm:col-span-2" : "";
  // Render a single shared <datalist> alongside text/number inputs so the
  // browser shows previously-typed values as suggestions.  Empty list = no
  // dropdown surfaces, but the input still works.
  const datalist = presets.length > 0 ? (
    <datalist id={datalistId} data-testid={`ie-presets-${f.field_name}`}>
      {presets.map((v) => <option key={v} value={v} />)}
    </datalist>
  ) : null;
  // 2026-02-25 hoisted Item Name short-circuit — return the plain
  // <input> BEFORE the switch on `f.field_type` so no dropdown /
  // multi-select / ChipMultiPicker / ComboBox wrapper can ever
  // wrap the Item Name field, regardless of what field_type the
  // schema declares. Wiring untouched: `onChange` still funnels
  // through the same setFieldValue handler bound to
  // values["Item Name"], and the three useEffect autobuilders
  // (~L962/993/1022) remain the sole path that ever populates
  // the field automatically.
  if (f.field_name === "Item Name") {
    return (<div className={spanClass} onBlur={onBlur} tabIndex={-1}>{label}
      <input
        id={id}
        data-testid={testid}
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="vp-input"
        placeholder={placeholder}
        autoComplete="off"
      />
      {errorNode}
    </div>);
  }
  switch (f.field_type) {
    case "textarea":
      return (<div className={spanClass}>{label}
        <textarea id={id} data-testid={testid} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} className="vp-textarea w-full" placeholder={placeholder} />
        {errorNode}
      </div>);
    case "number": {
      // Number-typed fields (Age / ABV / Vintage / Rating …) gain
      // dropdown-on-focus via a merged datalist of presets + previously-
      // saved values. Free-text entry still works — datalist is just a
      // suggestion surface, not a closed select.
      const numSuggestions = Array.from(new Set([
        ...(presets || []),
        ...(fieldValues || []),
      ])).filter(Boolean);
      return (<div className={spanClass}>{label}
        <input id={id} data-testid={testid} type="number" inputMode="decimal" list={numSuggestions.length ? datalistId : undefined} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} onBlur={onBlur} className="vp-input" placeholder={NUM_PLACEHOLDERS[f.field_name] || ""} />
        {numSuggestions.length > 0 && (
          <datalist id={datalistId} data-testid={`ie-presets-${f.field_name}`}>
            {numSuggestions.map((v) => <option key={v} value={v} />)}
          </datalist>
        )}
        {errorNode}
      </div>);
    }
    case "currency":
      return (<div className={spanClass}>{label}
        <input id={id} data-testid={testid} type="number" step="0.01" min="0" inputMode="decimal" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} onBlur={onBlur} className="vp-input" placeholder="0.00" />
        {errorNode}
      </div>);
    case "date":
      return (<div className={spanClass}>{label}
        <input id={id} data-testid={testid} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} className="vp-input" />
        {errorNode}
      </div>);
    case "checkbox":
      return (
        <label className={`flex items-center gap-2 cursor-pointer select-none ${spanClass}`}>
          <input id={id} data-testid={testid} type="checkbox" checked={value !== false} onChange={(e) => onChange(e.target.checked)} onBlur={onBlur} className="rounded accent-[hsl(var(--gold))]" />
          <span className="text-sm">{labelText}</span>
          {errorNode}
        </label>
      );
    case "dropdown": {
      // ComboBox renders dropdown-typed schema fields. Why not <select>?
      //   1. Shows the FULL option list on focus + chevron-driven open.
      //   2. Filters as the user types (substring match).
      //   3. "+ Add <value>" row when the typed value isn't in the list,
      //      so users can extend per-category enumerations on the fly.
      //   4. Newly-added values persist via /api/items/field-values and
      //      hydrate back into this exact field on subsequent loads.
      // Option pool = schema-declared options ∪ previously-saved values.
      const dropPool = Array.from(new Set([
        ...(f.options || []),
        ...(fieldValues || []),
      ])).filter(Boolean);
      return (<div className={spanClass}>{label}
        <ComboBox
          id={id}
          testid={testid}
          value={value || ""}
          onChange={onChange}
          onAdd={onAddNotify}
          options={dropPool}
          optimisticSet={optimisticSet}
          placeholder={placeholder || "—"}
        />
        {errorNode}
      </div>);
    }
    case "multi_select": {
      // Coerce legacy single-string values into an array so items created
      // BEFORE Grape(s) became a multi_select still load gracefully on
      // Edit Item. Splits on comma / semicolon for the common "Pinot Noir,
      // Chardonnay" pattern. Other multi_select fields benefit too.
      const arr = Array.isArray(value)
        ? value
        : (typeof value === "string" && value.trim()
            ? value.split(/[,;]\s*/).map((s) => s.trim()).filter(Boolean)
            : []);
      // Build the option pool — schema options + previously-saved values
      // for this exact field key + any custom values already on the item.
      const pool = [...(f.options || [])];
      for (const v of [...(fieldValues || []), ...arr]) {
        if (!pool.includes(v)) pool.push(v);
      }
      // Grape(s) gets the restored dropdown + chip picker UX. Other
      // multi_select fields keep the existing chip-grid behaviour (per the
      // task spec: "This fix applies only to the Grape(s) field").
      if (f.field_name === "Grape(s)") {
        return (<div className={spanClass} onBlur={onBlur} tabIndex={-1}>{label}
          <ChipMultiPicker
            id={id}
            testid={testid}
            value={arr}
            onChange={onChange}
            options={pool}
            placeholder={`Add ${f.field_name.toLowerCase()}…`}
          />
          {errorNode}
        </div>);
      }
      const toggle = (opt) => onChange(arr.includes(opt) ? arr.filter((v) => v !== opt) : [...arr, opt]);
      const addCustom = (raw) => {
        const t = String(raw || "").trim();
        if (!t) return;
        if (arr.includes(t)) return;
        onChange([...arr, t]);
      };
      return (<div className={spanClass} onBlur={onBlur} tabIndex={-1}>{label}
        <div data-testid={testid} className="flex flex-wrap gap-2">
          {pool.map((o) => {
            const active = arr.includes(o);
            return (
              <button
                type="button"
                key={o}
                onClick={() => toggle(o)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors duration-fast ${
                  active
                    ? "bg-[hsl(var(--gold))] border-[hsl(var(--gold))] text-[#1a1a1a]"
                    : "border-[hsl(var(--border-strong))] vp-muted hover:bg-[hsl(var(--muted))]"
                }`}
              >
                {o}
              </button>
            );
          })}
        </div>
        <MultiSelectCustomInput
          testid={`${testid}-add-custom`}
          placeholder={`Add custom ${f.field_name.toLowerCase()}…`}
          onAdd={addCustom}
        />
        {errorNode}
      </div>);
    }
    case "image":
      return (<div className={spanClass} onBlur={onBlur} tabIndex={-1}>{label}
        <ImageField id={id} testid={testid} value={value || ""} onChange={onChange} />
        {errorNode}
      </div>);
    case "text":
    default: {
      // Free-text / prose fields must NOT render a datalist — the browser
      // attaches a dropdown arrow indicator whenever `list=...` is set, even
      // for prose where suggestions make no sense. Hide the arrow for these.
      const NO_DATALIST = new Set([
        "Notes", "Note", "Description", "Comments", "Comment",
        "Remarks", "Tasting Notes",
      ]);
      const showDatalist = presets.length > 0 && !NO_DATALIST.has(f.field_name);
      // Item Name short-circuits at the top of DynamicField (see hoisted
      // guard above the `switch`) — no branch for it inside the text
      // case any more.
      if (comboDefaults) {
        // Build the merged option list: presets (already deduped + sorted by
        // the /api/presets endpoint) layered on top of common defaults +
        // previously-saved values for this exact field so the dropdown
        // covers canonical sizes AND user history even when one source is
        // empty.
        const merged = Array.from(new Set([...(comboDefaults || []), ...presets, ...fieldValues]));
        return (<div className={spanClass} onBlur={onBlur} tabIndex={-1}>{label}
          <ComboBox
            id={id}
            testid={testid}
            value={value || ""}
            onChange={onChange}
            onAdd={onAddNotify}
            options={merged}
            optimisticSet={optimisticSet}
            placeholder={placeholder}
          />
          {errorNode}
        </div>);
      }
      // Field-specific autosuggest / dropdown-on-focus for any text field
      // that has a populated `fieldValues` list (Region, Sub-Region, etc.).
      // Filters by the field key, not global values — that filtering is
      // already enforced upstream by the `/api/items/field-values?field=…`
      // endpoint, so we just hand the list to ComboBox.
      if (fieldValues.length > 0 && !NO_DATALIST.has(f.field_name)) {
        return (<div className={spanClass} onBlur={onBlur} tabIndex={-1}>{label}
          <ComboBox
            id={id}
            testid={testid}
            value={value || ""}
            onChange={onChange}
            onAdd={onAddNotify}
            options={fieldValues}
            optimisticSet={optimisticSet}
            placeholder={placeholder}
          />
          {errorNode}
        </div>);
      }
      return (<div className={spanClass}>{label}
        <input
          id={id}
          data-testid={testid}
          type="text"
          list={showDatalist ? datalistId : undefined}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          className="vp-input"
          placeholder={placeholder}
        />
        {showDatalist ? datalist : null}
        {errorNode}
      </div>);
    }
  }
}

/* ============================================================
   ComboBox is now exported from /app/frontend/src/components/ComboBox.jsx
   ============================================================ */


// Inline "add custom" affordance for multi_select chip pickers. Renders
// a tiny text input + Add button below the chip grid. Empty submissions
// and duplicates are no-ops (handled by the caller's onAdd guard).
function MultiSelectCustomInput({ testid, placeholder, onAdd }) {
  const [draft, setDraft] = useState("");
  const commit = () => {
    const t = draft.trim();
    if (!t) return;
    onAdd(t);
    setDraft("");
  };
  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap">
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
        }}
        placeholder={placeholder}
        data-testid={testid}
        className="vp-input !py-1.5 !text-xs flex-1 min-w-[180px]"
      />
      <button
        type="button"
        onClick={commit}
        disabled={!draft.trim()}
        data-testid={`${testid}-btn`}
        className="vp-btn vp-btn-secondary vp-btn-sm disabled:opacity-40"
      >
        <Plus size={12} /> Add
      </button>
    </div>
  );
}

// ChipMultiPicker — restored Grape(s) UX:
//   • Selected values render as removable chips at the top.
//   • A text input below opens a dropdown on focus showing all options
//     that are NOT already selected. Substring filter as the user types.
//   • Click a dropdown row → add to selection, keep input cleared and
//     dropdown open for fast multi-pick.
//   • Type a brand-new value + Enter (or click "+ Add 'X'") → add as a
//     custom chip. Future loads of the screen pick that value back up
//     via /api/items/field-values?field=Grape(s) and re-merge it into
//     the dropdown — so custom additions persist for future use.
//   • Click outside closes the dropdown.
function ChipMultiPicker({ id, testid, value = [], onChange, options = [], placeholder }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const wrapRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [open]);

  const remove = (v) => onChange(value.filter((x) => x !== v));
  const add = (v) => {
    const t = String(v || "").trim();
    if (!t) return;
    if (value.includes(t)) return;
    onChange([...value, t]);
  };

  const lowerDraft = draft.trim().toLowerCase();
  // Show options that aren't already chips, filtered by substring.
  const filtered = options
    .filter((o) => !value.includes(o))
    .filter((o) => !lowerDraft || o.toLowerCase().includes(lowerDraft));
  const exactMatch = filtered.some((o) => o.toLowerCase() === lowerDraft);
  const showAddRow = !!lowerDraft && !exactMatch && !value.some((v) => v.toLowerCase() === lowerDraft);

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2" data-testid={`${testid}-selected`}>
          {value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-[hsl(var(--gold))] text-[#1a1a1a] border border-[hsl(var(--gold))]"
            >
              <span>{v}</span>
              <button
                type="button"
                onClick={() => remove(v)}
                aria-label={`Remove ${v}`}
                data-testid={`${testid}-chip-remove-${v.replace(/\s+/g, "-").toLowerCase()}`}
                className="-mr-0.5 leading-none opacity-70 hover:opacity-100"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          id={id}
          data-testid={`${testid}-input`}
          type="text"
          value={draft}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setDraft(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (filtered.length > 0 && !showAddRow) {
                add(filtered[0]);
              } else if (lowerDraft) {
                add(draft);
              }
              setDraft("");
            } else if (e.key === "Escape") {
              setOpen(false);
            } else if (e.key === "Backspace" && !draft && value.length > 0) {
              // Quick remove last chip when input is empty.
              remove(value[value.length - 1]);
            }
          }}
          placeholder={placeholder}
          className="vp-input pr-9"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close" : "Open"}
          className="absolute inset-y-0 right-2 my-auto h-6 w-6 inline-flex items-center justify-center vp-muted hover:text-[hsl(var(--fg))]"
        >
          <ChevronDown size={16} className={`transition-transform duration-fast ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (filtered.length > 0 || showAddRow) && (
        <ul
          data-testid={`${testid}-dropdown`}
          className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-lg"
        >
          {filtered.map((o) => (
            <li
              key={o}
              role="option"
              tabIndex={0}
              onMouseDown={(e) => { e.preventDefault(); add(o); setDraft(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") { add(o); setDraft(""); } }}
              data-testid={`${testid}-option-${o.replace(/\s+/g, "-").toLowerCase()}`}
              className="cursor-pointer px-3 py-1.5 text-xs hover:bg-[hsl(var(--muted))]"
            >
              {o}
            </li>
          ))}
          {showAddRow && (
            <li
              role="option"
              tabIndex={0}
              onMouseDown={(e) => { e.preventDefault(); add(draft); setDraft(""); }}
              data-testid={`${testid}-add-custom`}
              className="cursor-pointer px-3 py-1.5 text-xs border-t border-[hsl(var(--border))] text-[hsl(var(--gold-strong))] hover:bg-[hsl(var(--muted))]"
            >
              + Add &ldquo;{draft.trim()}&rdquo;
            </li>
          )}
        </ul>
      )}
    </div>
  );
}





function ImageField({ id, testid, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image > 8 MB"); return; }
    setUploading(true);
    const r = new FileReader();
    r.onload = () => { onChange(r.result); setUploading(false); };
    r.onerror = () => { toast.error("Image upload failed."); setUploading(false); };
    r.readAsDataURL(file);
  };
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <input id={id} data-testid={testid} type="file" accept="image/*" onChange={onFile} disabled={uploading} className="vp-input !py-1.5 !text-sm disabled:opacity-60" />
      {uploading && (
        <span data-testid={`${testid}-uploading`} className="inline-flex items-center gap-1.5 text-xs vp-muted">
          <Loader2 size={14} className="animate-spin" /> Uploading…
        </span>
      )}
      {!uploading && value && <img src={value} alt="preview" className="h-12 w-12 object-cover rounded-md border border-[hsl(var(--border))]" />}
      {!uploading && value && (
        <button type="button" onClick={() => onChange("")} className="vp-btn vp-btn-ghost vp-btn-sm text-[hsl(var(--loss))]">
          Remove
        </button>
      )}
    </div>
  );
}
